/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable react/no-array-index-key */

import JSG from '@cedalo/jsg-ui';
import { Locale, NullTerm } from '@cedalo/parser';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormLabel,
	IconButton,
	Input,
	InputAdornment,
	MenuItem,
	Radio,
	RadioGroup,
	TextField
} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { graphManager } from '../../../GraphManager';
import { intl } from '../../../helper/IntlGlobalProvider';
import DialogButton from '../../utils/DialogButton';
import CellRangeComponent from '../CellRangeComponent';
import ColorComponent from '../ColorComponent';
import {useAsyncReference, useJSGNotification} from '../../utils/JSGHooks';
import ValueRangesDialog from '../dialogs/ValueRangesDialog';
import { StreamSheetAccordion } from '../../utils/StreamSheetAccordion';

const markerSizes = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const MyInputComponent = React.forwardRef((props, ref) => {
	const { component: Component, ...other } = props;
	React.useImperativeHandle(ref, () => ({
		focus: () => {}
	}));
	return <Component {...other} />;
});

export function StreamChartSettings(props) {
	const [showValueRanges, setShowValueRanges] = useState(false);
	const [hole, setHole] = useState(0);
	const [rotation, setRotation] = useState(0);
	const [startAngle, setStartAngle] = useState(0);
	const [endAngle, setEndAngle] = useState(0);
	const [, setRandom] = useState(-1);
	const [expanded, setExpanded] = useState('');
	const [axisIndex, setAxisIndex] = useState(0);
	const [seriesIndex, setSeriesIndex] = useState(0);
	const [pointIndex, setPointIndex] = useState(-1);
	const [axisType, setAxisType] = useState('xAxis');
	const [lastSelection, setLastSelection] = useAsyncReference({});

	const { chart, sheetView, shapeSheet } = props;
	const isGaugeChart = () => {
		return !!chart.getFirstSerieOfType('gauge');
	};

	const onShapeSelection = () => {
		const viewer = graphManager.getGraphViewer();
		const selection = viewer.getSelection();
		if (selection.length !== 1) {
			return;
		}
		const view = selection[0].getView();

		if (!view.chartSelection) {
			return;
		}

		if (lastSelection.current.element === view.chartSelection.element &&
			lastSelection.current.index === view.chartSelection.index &&
			lastSelection.current.pointIndex === view.chartSelection.pointIndex) {
			return;
		}

		switch (view.chartSelection.element) {
		case 'point':
				setExpanded('panelSeries');
				setSeriesIndex(view.chartSelection.index);
				setPointIndex(view.chartSelection.pointIndex);
				break;
			case 'series':
			case 'serieslabel':
				setExpanded('panelSeries');
				setSeriesIndex(view.chartSelection.index);
				setPointIndex(-1);
				break;
			case 'xAxis':
			case 'xAxisTitle':
			case 'xAxisGrid':
				setExpanded('panelAxes');
				setAxisType('xAxis');
				setAxisIndex(view.chartSelection.index);
				break;
			case 'yAxis':
			case 'yAxisTitle':
			case 'yAxisGrid':
				setExpanded('panelAxes');
				setAxisType('yAxis');
				setAxisIndex(view.chartSelection.index);
				break;
			case 'legend':
				setExpanded('panelLegend');
				break;
			case 'plot':
				setExpanded('panelPlot');
				break;
			case 'hilolines':
			case 'upbars':
			case 'downbars':
				setExpanded('panelChart');
				break;
			case 'title':
				setExpanded('panelTitle');
				break;
			default:
				break;
		}
		setLastSelection({...view.chartSelection});
	};

	useEffect(() => {
		setHole(chart.chart.hole);
		setRotation(chart.chart.rotation);
		setStartAngle(chart.chart.startAngle);
		setEndAngle(chart.chart.endAngle);
		setAxisType(isGaugeChart() ? 'yAxis' : 'xAxis');
		setAxisIndex(0);
		setPointIndex(-1);
		setSeriesIndex(0);
		onShapeSelection();
	}, [chart]);

	useJSGNotification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, onShapeSelection);

	const updateState = (notify = false) => {
		// to trigger render from chart changes
		setRandom(Math.random());
		if (notify) {
			JSG.NotificationCenter.getInstance().send(
				new JSG.Notification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, chart)
			);
		}
	};

	const prepareCommand = (key) => {
		return chart.prepareCommand(key);
	};

	const isHorizontalChart = () => {
		return chart.isHorizontalChart();
	};

	const isLineChart = () => {
		return !!chart.getFirstSerieOfType('line');
	};

	const isStateChart = () => {
		return !!chart.getFirstSerieOfType('state');
	};

	const isBubbleChart = () => {
		return !!chart.getFirstSerieOfType('bubble');
	};

	const usesCValues = (serie) => {
		return serie.type === 'bubble' || serie.type === 'state' || serie.type === 'heatmap' || serie.type === 'map';
	};

	const isFunnelChart = () => {
		return !!chart.getFirstSerieOfType('funnelbar') || !!chart.getFirstSerieOfType('funnelcolumn');
	};

	const getAxisData = () => {
		return chart.getDataFromSelection({ index: axisIndex, element: axisType });
	};

	const getSeriesData = () => {
		return chart.getDataFromSelection({ index: seriesIndex, element: 'series' });
	};

	const getExpression = (value) => {
		let expr;

		if (value.length && value.charAt(0) === '=') {
			value = value.substring(1);
			try {
				JSG.FormulaParser.context.separators = JSG.getParserLocaleSettings().separators;
				const term = JSG.FormulaParser.parseSheet(value, shapeSheet.model);
				JSG.FormulaParser.context.separators = Locale.EN.separators;
				if (term) {
					if (term.isStatic) {
						expr = JSG.ExpressionHelper.createExpressionFromValueTerm(term);
					} else {
						const formula = term.toLocaleString('en', {
							sheet: shapeSheet.model,
							useName: true,
							forceName: true
						});
						expr = new JSG.Expression(0, formula);
					}
				}
			} catch (e) {
				sheetView.notifyMessage({
					message: e.message,
					focusIndex: e.index !== undefined ? e.index + 1 : 1
				});
			}
		} else {
			const term = JSG.FormulaParser.parseValue(value, shapeSheet.model);
			expr = JSG.ExpressionHelper.createExpressionFromValueTerm(term);
		}

		return expr;
	};

	const finishCommand = (cmd, key, notify = false, delay = false) => {
		chart.finishCommand(cmd, key);
		chart._lastJSON = undefined;
		graphManager.getGraphViewer().getInteractionHandler().execute(cmd);
		if (delay) {
			// wait for server response
			setTimeout(() => {
				updateState();
			}, 300);
		} else {
			updateState(notify);
		}
	};

	const handleDataModeChange = (event) => {
		const cmd = prepareCommand('chart');
		chart.chart.dataMode = event.target.value;
		finishCommand(cmd, 'chart');
	};

	const handleChartCoharentChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.coharentData = state;
		finishCommand(cmd, 'chart');
	};

	const updateFormulas = (formula, cmdChart) => {
		const viewer = graphManager.getGraphViewer();
		chart.updateFormulas(viewer, formula, cmdChart);
		updateState();
	};

	const handleChartFormulaBlur = (event) => {
		const formula = event.target.textContent.replace(/^=/, '');
		const cmdChart = prepareCommand('chart');
		updateFormulas(formula, cmdChart);
	};

	const handleChartDataInRowsChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.dataInRows = state;
		updateFormulas(chart.chart.formula.getFormula(), cmd);
	};

	const handleChartFirstSeriesLabelsChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.firstSeriesLabels = state;
		updateFormulas(chart.chart.formula.getFormula(), cmd);
		updateState();
	};

	const handleChartFirstCategoryLabelsChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.firstCategoryLabels = state;
		updateFormulas(chart.chart.formula.getFormula(), cmd);
		updateState();
	};

	const handleChartGaugePointerChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.gaugePointer = state;
		finishCommand(cmd, 'chart');
	};

	const handleChartStackedChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.stacked = state;
		if (state === false) {
			chart.chart.relative = false;
		}
		finishCommand(cmd, 'chart');
	};

	const handleChartStepChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.step = state;
		finishCommand(cmd, 'chart');
	};

	const handleChartSegmentsChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.segments = state;
		finishCommand(cmd, 'chart');
	};

	const handleChartHundredPercentChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.relative = state;
		if (state === true) {
			chart.chart.stacked = true;
		}
		finishCommand(cmd, 'chart');
	};

	const handleChartRotationBlur = () => {
		setRotation(chart.chart.rotation);
	};

	const handleChartRotationChange = (event) => {
		const value = Number(event.target.value);

		if (value >= 10 && value <= 90) {
			const cmd = prepareCommand('chart');
			chart.chart.rotation = JSG.MathUtils.toRadians(value);
			finishCommand(cmd, 'chart');
		}
		setRotation(JSG.MathUtils.toRadians(value));
	};

	const handleChartHoleBlur = () => {
		setHole(chart.chart.hole);
	};

	const handleChartHoleChange = (event) => {
		const value = Number(event.target.value);

		if (value >= 0 && value <= 95) {
			const cmd = prepareCommand('chart');
			chart.chart.hole = value / 100;
			finishCommand(cmd, 'chart');
		}
		setHole(value / 100);
	};

	const checkAngles = (startAng, endAng, start) => {
		if (endAng - startAng > Math.PI * 2 || endAng - startAng < JSG.MathUtils.toRadians(5)) {
			return (
				<FormattedMessage
					id="StreamChartSettings.InvalidAngleRange"
					defaultMessage="The angle range must be between 5 and 360 degrees!"
				/>
			);
		}

		if (start && (startAng > Math.PI * 2 || startAng < 0)) {
			return (
				<FormattedMessage
					id="StreamChartSettings.InvalidAngle360"
					defaultMessage="The start range must be 0 and 360 degrees!"
				/>
			);
		}

		if (!start && (endAng > Math.PI * 3 || endAng < 0)) {
			return (
				<FormattedMessage
					id="StreamChartSettings.InvalidAngle540"
					defaultMessage="The end angle must be 0 and 540 degrees!"
				/>
			);
		}

		return '';
	};

	const handleChartStartAngleChange = (event) => {
		const value = JSG.MathUtils.toRadians(Number(event.target.value));

		if (checkAngles(value, endAngle, true) === '') {
			const cmd = prepareCommand('chart');
			chart.chart.startAngle = value;
			finishCommand(cmd, 'chart');
		}
		setStartAngle(value);
	};

	const handleChartStartAngleBlur = () => {
		setStartAngle(chart.chart.startAngle);
	};

	const handleChartEndAngleChange = (event) => {
		const value = JSG.MathUtils.toRadians(Number(event.target.value));

		if (checkAngles(startAngle, value, false) === '') {
			const cmd = prepareCommand('chart');
			chart.chart.endAngle = value;
			finishCommand(cmd, 'chart');
		}

		setEndAngle(value);
	};

	const handleChartEndAngleBlur = () => {
		setEndAngle(chart.chart.endAngle);
	};

	const handleTooltipsChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.tooltips = state;
		finishCommand(cmd, 'chart');
	};

	const handleVisibleChange = (event, state, data, id) => {
		const cmd = prepareCommand(id);
		data.visible = state;
		finishCommand(cmd, id);
	};

	const handleUpDownBarsChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.upBars.visible = state;
		chart.chart.downBars.visible = state;
		finishCommand(cmd, 'chart');
	};

	const handleVaryByCategoriesChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.varyByCategories = state;
		finishCommand(cmd, 'chart');
	};

	const handleMenuVisibleChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.menuVisible = state;
		finishCommand(cmd, 'chart');
	};

	const handleMapZoomChange = (event, state) => {
		const cmd = prepareCommand('chart');
		chart.chart.mapZoom = state;
		finishCommand(cmd, 'chart');
	};

	const handleVaryByThresholdChange = (event) => {
		const cmd = prepareCommand('chart');
		chart.chart.varyByThreshold = event.target.value;
		finishCommand(cmd, 'chart');
	};

	const handleTemplateChange = (event) => {
		const cmd = prepareCommand('chart');
		chart.chart.template = event.target.value;
		finishCommand(cmd, 'chart');

		const viewer = graphManager.getGraphViewer();
		const selection = viewer.getSelection();
		if (selection.length !== 1) {
			return;
		}
		const view = selection[0].getView();
		view.onSelectionChange();

		const attributesMap = new JSG.Dictionary();
		attributesMap.put(
			JSG.FormatAttributes.FILLCOLOR,
			JSG.SheetPlotNode.templates[chart.chart.template].chart.format.fillColor
		);
		attributesMap.put(JSG.FormatAttributes.FILLSTYLE, JSG.FormatAttributes.FillStyle.SOLID);

		graphManager.getGraphViewer().getInteractionHandler().applyFormatMap(attributesMap);
	};

	const handleChartShapesFormulaBlur = (event) => {
		const cmd = prepareCommand('chart');

		const expr = getExpression(event.target.textContent);
		if (expr) {
			chart.chart.shapesJson = expr;
		}

		finishCommand(cmd, 'chart');
	};


	const handleLegendAlignChange = (event, value) => {
		const cmd = prepareCommand('legend');
		chart.legend.align = value;
		finishCommand(cmd, 'legend');
	};

	const handleLegendTitleFormulaBlur = (event) => {
		const cmdLegend = prepareCommand('legend');

		const expr = getExpression(event.target.textContent);
		if (expr) {
			chart.legend.formulaTitle = expr;
		}

		finishCommand(cmdLegend, 'legend');
	};

	const handleTitleFormulaBlur = (event) => {
		const cmdLegend = prepareCommand('title');

		const expr = getExpression(event.target.textContent);
		if (expr) {
			chart.title.formula = expr;
		}

		finishCommand(cmdLegend, 'title');
	};

	const handleAxisTitleFormulaBlur = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();

		const expr = getExpression(event.target.textContent);
		if (expr) {
			data.title.formula = expr;
		}

		finishCommand(cmd, 'axes');
	};

	const handleTitleAlignChange = (event, value) => {
		const cmd = prepareCommand('title');
		chart.title.align = value;
		finishCommand(cmd, 'title');
	};

	const handleSeriesTimeSeriesChange = (event, series, state) => {
		const cmd = prepareCommand('series');
		series.timeSeries = state;
		finishCommand(cmd, 'series', false, true);
	};

	const handleCategoriesLabelFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');

		chart.chart.formula = new JSG.Expression('');
		const expr = getExpression(event.target.textContent);
		if (expr) {
			series.formulaXLabel = expr;
		}

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesLabelFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');

		chart.chart.formula = new JSG.Expression('');
		const expr = getExpression(event.target.textContent);
		if (expr) {
			series.formulaYLabel = expr;
		}

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesTimeValuesFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');

		chart.chart.formula = new JSG.Expression('');
		const expr = getExpression(event.target.textContent);
		if (expr) {
			series.formulaTime = expr;
		}

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesTimeXFieldFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');

		chart.chart.formula = new JSG.Expression('');
		const expr = getExpression(event.target.textContent);
		if (expr) {
			series.formulaTimeXKey = expr;
		}

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesTimeYFieldFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');

		chart.chart.formula = new JSG.Expression('');
		const expr = getExpression(event.target.textContent);
		if (expr) {
			series.formulaTimeYKey = expr;
		}

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesTimeCFieldFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');

		chart.chart.formula = new JSG.Expression('');
		const expr = getExpression(event.target.textContent);
		if (expr) {
			series.formulaTimeCKey = expr;
		}

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesXYCategoriesFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');
		const formula = event.target.textContent;

		chart.chart.formula = new JSG.Expression('');

		const formulas = formula.split(JSG.getParserLocaleSettings().separators.parameter === ',' ? ';' : ',');

		series.formulaXYCategories = [];
		formulas.forEach((form) => {
			const expr = getExpression(form);
			if (expr) {
				series.formulaXYCategories.push(expr);
			}
		});

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesCategoriesFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');
		const formula = event.target.textContent;

		chart.chart.formula = new JSG.Expression('');

		const formulas = formula.split(JSG.getParserLocaleSettings().separators.parameter === ',' ? ';' : ',');

		series.formulaXValues = [];
		formulas.forEach((form) => {
			const expr = getExpression(form);
			if (expr) {
				series.formulaXValues.push(expr);
			}
		});

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesValuesFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');
		const formula = event.target.textContent;

		chart.chart.formula = new JSG.Expression('');

		const formulas = formula.split(JSG.getParserLocaleSettings().separators.parameter === ',' ? ';' : ',');

		series.formulaYValues = [];
		formulas.forEach((form) => {
			const expr = getExpression(form);
			if (expr) {
				series.formulaYValues.push(expr);
			}
		});

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesExtraValuesFormulaBlur = (event, series) => {
		const cmd = prepareCommand('series');
		const formula = event.target.textContent;

		chart.chart.formula = new JSG.Expression('');

		const formulas = formula.split(JSG.getParserLocaleSettings().separators.parameter === ',' ? ';' : ',');

		series.formulaCValues = [];
		formulas.forEach((form) => {
			const expr = getExpression(form);
			if (expr) {
				series.formulaCValues.push(expr);
			}
		});

		finishCommand(cmd, 'series', false, true);
	};

	const handleSeriesDuplicate = (index) => {
		const cmd = prepareCommand('series');

		const copy = chart.series[index].copy();
		JSG.Arrays.insertAt(chart.series, index, copy);

		finishCommand(cmd, 'series');
	};

	const handleSeriesDelete = (index) => {
		const cmd = prepareCommand('series');

		JSG.Arrays.removeAt(chart.series, index);

		finishCommand(cmd, 'series');
	};

	const handleSeriesMoveDown = (index) => {
		const cmd = prepareCommand('series');

		JSG.Arrays.move(chart.series, index, index + 1);

		finishCommand(cmd, 'series');
	};

	const handleSeriesMoveUp = (index) => {
		const cmd = prepareCommand('series');

		JSG.Arrays.move(chart.series, index, index - 1);

		finishCommand(cmd, 'series');
	};

	const handleGridlineVisibleChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.gridVisible = state;
		finishCommand(cmd, 'axes');
	};

	const handleValueRangesVisibleChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.valueRangesVisible = state;
		finishCommand(cmd, 'axes');
	};

	const handleAxisLabelRotationChange = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.format.fontRotation = event.target.value;
		finishCommand(cmd, 'axes');
	};

	const handleAxisLabelDistanceChange = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.labelDistance = event.target.value;
		finishCommand(cmd, 'axes');
	};

	const handleAxisVisibleChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.visible = state;
		finishCommand(cmd, 'axes');
	};

	const handleAxisTypeChange = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.type = event.target.value;
		finishCommand(cmd, 'axes');
	};

	const replaceTerm = (event, expression, index) => {
		const formula = event.target.textContent;
		const newExpression = shapeSheet.textToExpression(formula);
		const term = expression.getTerm();

		if (term && newExpression) {
			for (let i = term.params.length; i < index; i += 1) {
				term.params[i] = new NullTerm();
			}

			term.params[index] = newExpression.term;
			expression.correctFormula(shapeSheet.model, true);
		}
	};

	const handleAxisMinimumFormulaBlur = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();

		replaceTerm(event, data.formula, 0);
		finishCommand(cmd, 'axes', true);
	};

	const handleAxisMaximumFormulaBlur = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();

		replaceTerm(event, data.formula, 1);
		finishCommand(cmd, 'axes', true);
	};

	const handleAxisStepFormulaBlur = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();

		replaceTerm(event, data.formula, 2);
		finishCommand(cmd, 'axes', true);
	};

	const handleAxisTimeStepFormulaBlur = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();

		replaceTerm(event, data.formula, 3);
		finishCommand(cmd, 'axes', true);
	};

	const handleAxisTitleVisibleChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.title.visible = state;
		finishCommand(cmd, 'axes');
	};

	// const handleAxisTitleTitleVisibleChange = (event, state) => {
	// 	const cmd = prepareCommand('axes');
	// 	const data = getAxisData();
	// 	data.visible = state;
	// 	finishCommand(cmd, 'axes');
	// };
	//
	const handleAxisInvertChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.invert = state;
		finishCommand(cmd, 'axes');
	};

	const handleAxisAutoZeroChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.autoZero = state;
		if (state) {
			data.valueMinMax = false;
		}
		finishCommand(cmd, 'axes');
	};

	const handleAxisValueMinMaxChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.valueMinMax = state;
		data.autoZero = !state;

		finishCommand(cmd, 'axes');
	};

	const handleAxisNumberformatLinkChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.format.linkNumberFormat = state;
		finishCommand(cmd, 'axes');
	};

	const handleAxisAllowZoomChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.allowZoom = state;
		finishCommand(cmd, 'axes');
	};

	const handleAxisUpdateZoomChange = (event, state) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.updateZoom = state;
		finishCommand(cmd, 'axes');
	};

	const handleAxisZoomGroupChange = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.zoomGroup = event.target.value;
		finishCommand(cmd, 'axes');
	};

	const handleAxisAlignChange = (event) => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		data.align = event.target.value;
		finishCommand(cmd, 'axes');
	};

	const handleEditValueRanges = (state, ranges) => {
		setShowValueRanges(state);
		if (ranges !== undefined) {
			const cmd = prepareCommand('axes');
			const data = getAxisData();
			data.valueRanges = ranges;
			finishCommand(cmd, 'axes');
		}
	};

	const handleEditChartValueRanges = (state, ranges) => {
		setShowValueRanges(state);
		if (ranges !== undefined) {
			const cmd = prepareCommand('chart');
			chart.chart.valueRanges = ranges;
			finishCommand(cmd, 'chart');
		}
	};

	const isAxisDeletable = (axis) => {
		if (axisType === 'xAxis') {
			return chart.xAxes.length > 1 && chart.xAxes.indexOf(axis) > 0;
		}

		return chart.yAxes.length > 1 && chart.yAxes.indexOf(axis) > 0;
	};

	const handleAddAxis = () => {
		const cmd = prepareCommand('axes');
		const data = getAxisData();
		const name = chart.getUniqueAxisName(axisType);
		const axis = new JSG.ChartAxis(name, data.type, data.align, 500);

		if (axisType === 'xAxis') {
			axis.align = chart.xAxes.length % 2 ? 'top' : 'bottom';
			chart.xAxes.push(axis);
		} else {
			if (chart.isGauge()) {
				axis.align = 'radialinside';
			} else {
				axis.align = chart.yAxes.length % 2 ? 'right' : 'left';
			}
			chart.yAxes.push(axis);
		}
		finishCommand(cmd, 'axes');
	};
	const handleDeleteAxis = (axis) => {
		const cmd = prepareCommand('axes');

		if (axisType === 'xAxis') {
			chart.reAssignAxis(axis, true);
			JSG.Arrays.remove(chart.xAxes, axis);
		} else {
			chart.reAssignAxis(axis, false);
			JSG.Arrays.remove(chart.yAxes, axis);
		}

		finishCommand(cmd, 'axes');

		setAxisIndex(0);
	};

	const handleSeriesXAxisChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.xAxis = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesYAxisChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.yAxis = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesTypeChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.type = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesIndexChange = (event) => {
		setSeriesIndex(Number(event.target.value));
	};

	const handlePointIndexChange = (event) => {
		setPointIndex(Number(event.target.value));
	};

	const handlePointRemoveSettings = () => {
		const cmd = prepareCommand('series');
		const serie = getSeriesData();

		if (pointIndex !== -1) {
			serie.points.splice(pointIndex, 1);
		}

		finishCommand(cmd, 'series');
	};

	const handleAxesIndexChange = (event) => {
		const index = Number(event.target.value);

		if (index < chart.xAxes.length) {
			setAxisIndex(Number(event.target.value));
			setAxisType('xAxis');
		} else {
			const xCnt = chart.xAxes.length;
			setAxisIndex(Number(event.target.value) - xCnt);
			setAxisType('yAxis');
		}
	};

	const handleSeriesTooltipContentChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.tooltip = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesPointerTypeChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.pointerType = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesPointerLengthChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.pointerLength = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesMarkerStyleChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.marker.style = event.target.value;
			data.points.forEach((point) => {
				if (point.marker) {
					point.marker.style = undefined;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.marker) {
				point.marker = new JSG.ChartMarker();
			}
			point.marker.style = event.target.value;
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesMarkerSizeChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.marker.size = Number(event.target.value);
			data.points.forEach((point) => {
				if (point.marker) {
					point.marker.size = undefined;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.marker) {
				point.marker = new JSG.ChartMarker();
			}
			point.marker.size = Number(event.target.value);
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesMarkerFillColorChange = (color) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.marker.fillColor = color;
			data.points.forEach((point) => {
				if (point.marker) {
					point.marker.fillColor = undefined;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.marker) {
				point.marker = new JSG.ChartMarker();
			}
			point.marker.fillColor = color;
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesMarkerLineColorChange = (color) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.marker.lineColor = color;
			data.points.forEach((point) => {
				if (point.marker) {
					point.marker.lineColor = undefined;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.marker) {
				point.marker = new JSG.ChartMarker();
			}
			point.marker.lineColor = color;
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesMapName = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.map.name = event.target.value;
		data.map.mapData = undefined;
		data.map.requesting = undefined;
		finishCommand(cmd, 'series');

		// map needs some time to load then render map properties
		setTimeout(() => {
			updateState();
		}, 500);
	};

	const handleSeriesMapDisplayType = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.map.displayType = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesMapChartType = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.map.chartType = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesMapLabelChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.map.label = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesCopyMapLabels = () => {
		const data = getSeriesData();

		if (!data.map || !data.map.mapData) {
			return;
		}

		const features = data.map.mapData.features;
		const names = [];

		features.forEach((feature) => {
			const name = feature.properties[data.map.label] || '';
			if (name && names.indexOf(name) === -1) {
				names.push(name);
			}
		});
		names.sort();

			const val = intl.formatMessage({ id: 'StreamChartSettings.Values' }, {});
		let clipData = `\t${val}\n`;

		names.forEach((key) => {
			clipData += `${key}\n`;
		});

		sheetView.copyToClipboard(clipData);
	};

	const handlePointSumChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		const point = chart.getDataPoint(data, pointIndex);
		point.pointSum = state ? true : undefined;

		finishCommand(cmd, 'series');
	};

	const handleSeriesSmoothChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.smooth = state;
		finishCommand(cmd, 'series');
	};

	const handleSeriesAutoSumChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.autoSum = state;
		finishCommand(cmd, 'series');
	};

	const handleSeriesInnerPointsChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.innerPoints = state;
		finishCommand(cmd, 'series');
	};

	const handleSeriesOuterPointsChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.outerPoints = state;
		finishCommand(cmd, 'series');
	};

	const handleSeriesAverageChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.average = state;
		finishCommand(cmd, 'series');
	};

	const handleSeriesVisibleChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.visible = state;
		finishCommand(cmd, 'series');
	};

	const getAxisAlignOptions = () => {
		if (isGaugeChart()) {
			return [
				<MenuItem value="radialoutside" key={1}>
					<FormattedMessage id="StreamChartSettings.AxisOutside" defaultMessage="Outside" />
				</MenuItem>,
				<MenuItem value="radialinside" key={2}>
					<FormattedMessage id="StreamChartSettings.AxisInside" defaultMessage="Inside" />
				</MenuItem>
			];
		}

		if ((isHorizontalChart() && axisType === 'xAxis') || (!isHorizontalChart() && axisType === 'yAxis')) {
			return [
				<MenuItem value="left" key={1}>
					<FormattedMessage id="StreamChartSettings.AxisLeft" defaultMessage="Left" />
				</MenuItem>,
				<MenuItem value="right" key={2}>
					<FormattedMessage id="StreamChartSettings.AxisRight" defaultMessage="Right" />
				</MenuItem>
			];
		}

		return [
			<MenuItem value="bottom" key={4}>
				<FormattedMessage id="StreamChartSettings.AxisBottom" defaultMessage="Bottom" />
			</MenuItem>,
			<MenuItem value="top" key={3}>
				<FormattedMessage id="StreamChartSettings.AxisTop" defaultMessage="Top" />
			</MenuItem>
		];
	};

	const getLabelPositionOptions = (serie) => {
		switch (serie.type) {
			case 'pie':
				return [
					<MenuItem value="inner" key={2}>
						<FormattedMessage id="StreamChartSettings.InsideCircle" defaultMessage="At inner Circle" />
					</MenuItem>,
					<MenuItem value="center" key={3}>
						<FormattedMessage id="StreamChartSettings.Center" defaultMessage="Center" />
					</MenuItem>,
					<MenuItem value="outer" key={4}>
						<FormattedMessage id="StreamChartSettings.OutsideCircle" defaultMessage="At outer Circle" />
					</MenuItem>
				];
			case 'doughnut':
			case 'gauge':
			case 'heatmap':
				return [
					<MenuItem value="center" key={3}>
						<FormattedMessage id="StreamChartSettings.Center" defaultMessage="Center" />
					</MenuItem>
				];
			case 'line':
			case 'profile':
			case 'scatter':
			case 'boxplot':
			case 'bubble':
			case 'map':
				return [
					<MenuItem value="left" key={1}>
						<FormattedMessage id="StreamChartSettings.Left" defaultMessage="Left" />
					</MenuItem>,
					<MenuItem value="above" key={2}>
						<FormattedMessage id="StreamChartSettings.Above" defaultMessage="Above" />
					</MenuItem>,
					<MenuItem value="center" key={3}>
						<FormattedMessage id="StreamChartSettings.Center" defaultMessage="Center" />
					</MenuItem>,
					<MenuItem value="right" key={4}>
						<FormattedMessage id="StreamChartSettings.Right" defaultMessage="Right" />
					</MenuItem>,
					<MenuItem value="below" key={5}>
						<FormattedMessage id="StreamChartSettings.Below" defaultMessage="Below" />
					</MenuItem>
				];
			default:
				return [
					<MenuItem value="start" key={2}>
						<FormattedMessage id="StreamChartSettings.Start" defaultMessage="At Start" />
					</MenuItem>,
					<MenuItem value="center" key={3}>
						<FormattedMessage id="StreamChartSettings.Center" defaultMessage="Center" />
					</MenuItem>,
					<MenuItem value="end" key={4}>
						<FormattedMessage id="StreamChartSettings.End" defaultMessage="At End" />
					</MenuItem>,
					<MenuItem value="behindend" key={5}>
						<FormattedMessage id="StreamChartSettings.BehindEnd" defaultMessage="Behind End" />
					</MenuItem>
				];
		}
	};

	const handleSeriesDataLabelsChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		let dataLabel;

		if (pointIndex === -1) {
			dataLabel = data.dataLabel;
			dataLabel.visible = state;
			if (
				dataLabel.visible &&
				!dataLabel.content.cat &&
				!dataLabel.content.x &&
				!dataLabel.content.y &&
				!dataLabel.content.id &&
				!dataLabel.content.radius &&
				!dataLabel.content.state &&
				!dataLabel.content.series
			) {
				dataLabel.content.y = true;
			}
			data.points.forEach((point) => {
				if (point.dataLabel) {
					point.dataLabel.visible = undefined;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.dataLabel) {
				point.dataLabel = new JSG.ChartDataLabel();
				point.dataLabel.position = data.dataLabel.position;
			}
			point.dataLabel.visible = state;
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesDataLabelRotationChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();
		data.dataLabel.format.fontRotation = event.target.value;
		finishCommand(cmd, 'series');
	};

	const handleSeriesDataLabelsContentChange = (event, type, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.dataLabel.content[type] = state;

			data.points.forEach((point) => {
				if (point.dataLabel) {
					point.dataLabel.content[type] = state;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.dataLabel) {
				point.dataLabel = new JSG.ChartDataLabel();
			}
			point.dataLabel.content[type] = state;
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesDataLabelsSeparatorChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.dataLabel.separator = event.target.value;

			data.points.forEach((point) => {
				if (point.dataLabel) {
					point.dataLabel.separator = event.target.value;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.dataLabel) {
				point.dataLabel = new JSG.ChartDataLabel();
			}
			point.dataLabel.separator = event.target.value;
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesDataLabelsPositionChange = (event) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.dataLabel.position = event.target.value;

			data.points.forEach((point) => {
				if (point.dataLabel) {
					point.dataLabel.position = event.target.value;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.dataLabel) {
				point.dataLabel = new JSG.ChartDataLabel();
			}
			point.dataLabel.position = event.target.value;
		}

		finishCommand(cmd, 'series');
	};

	const handleSeriesDataLabelsLinkChange = (event, state) => {
		const cmd = prepareCommand('series');
		const data = getSeriesData();

		if (pointIndex === -1) {
			data.dataLabel.format.linkNumberFormat = state;

			data.points.forEach((point) => {
				if (point.dataLabel) {
					point.dataLabel.format.linkNumberFormat = state;
				}
			});
		} else {
			const point = chart.getDataPoint(data, pointIndex);
			if (!point.dataLabel) {
				point.dataLabel = new JSG.ChartDataLabel();
			}
			point.dataLabel.format.linkNumberFormat = state;
		}

		finishCommand(cmd, 'series');
	};

	// const translateTitle = (title) => {
	// 	switch (title) {
	// 		case 'Chart':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.Chart' }, {});
	// 		case 'title':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.Title' }, {});
	// 		case 'plot':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.Plot' }, {});
	// 		case 'legend':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.Legend' }, {});
	// 		case 'axes':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.AxisProperties' }, {});
	// 		case 'axesgrid':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.Grid' }, {});
	// 		case 'axestitle':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.AxisTitle' }, {});
	// 		case 'series':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.Series' }, {});
	// 		case 'point':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.Point' }, {});
	// 		case 'serieslabel':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.SeriesLabel' }, {});
	// 		case 'downbars':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.DownBars' }, {});
	// 		case 'upbars':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.UpBars' }, {});
	// 		case 'hilolines':
	// 			return intl.formatMessage({ id: 'StreamChartSettings.HiLoLines' }, {});
	// 		default:
	// 			return title;
	// 	}
	// }

	const getMarkerStyle = (data) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].marker &&
			data.points[pointIndex].marker._style !== undefined
		) {
			return data.points[pointIndex].marker.style;
		}

		return data.marker.style;
	};

	const getMarkerSize = (data) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].marker &&
			data.points[pointIndex].marker._size !== undefined
		) {
			return data.points[pointIndex].marker.size;
		}

		return data.marker.size;
	};

	const getMarkerLineColor = (data) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].marker &&
			data.points[pointIndex].marker.lineColor !== undefined
		) {
			return data.points[pointIndex].marker.lineColor;
		}

		return data.marker.lineColor || chart.getTemplate().series.getLineForIndex(seriesIndex);
	};

	const getMarkerFillColor = (data) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].marker &&
			data.points[pointIndex].marker.fillColor !== undefined
		) {
			return data.points[pointIndex].marker.fillColor;
		}

		return data.marker.fillColor || chart.getTemplate().series.getFillForIndex(seriesIndex);
	};

	const getDataLabelVisible = (data) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].dataLabel &&
			data.points[pointIndex].dataLabel.visible !== undefined
		) {
			return data.points[pointIndex].dataLabel.visible;
		}

		return data.dataLabel.visible === true;
	};

	const getDataLabelContent = (data, type) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].dataLabel &&
			data.points[pointIndex].dataLabel.content[type] !== undefined
		) {
			return data.points[pointIndex].dataLabel.content[type];
		}

		return data.dataLabel.content[type] === true;
	};

	const getDataLabelPosition = (data) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].dataLabel &&
			data.points[pointIndex].dataLabel.position !== undefined
		) {
			return data.points[pointIndex].dataLabel.position;
		}

		return data.dataLabel.position;
	};

	const getDataLabelSeparator = (data) => {
		if (
			pointIndex !== -1 &&
			data.points[pointIndex] &&
			data.points[pointIndex].dataLabel &&
			data.points[pointIndex].dataLabel.separator !== undefined
		) {
			return data.points[pointIndex].dataLabel.separator;
		}

		return data.dataLabel.separator;
	};

	const getPointSum = (data) => {
		return !!(pointIndex !== -1 && data.points[pointIndex] && data.points[pointIndex].pointSum);
	};

	const getLabel = (series) => {
		const ref = chart.getDataSourceInfo(series);
		if (ref && ref.yName !== undefined) {
			return ref.yName;
		}

		return String(chart.series.indexOf(series) + 1);
	};

	const getFormulaParameter = (expr, index, sheet) => {
		const term = expr.getTerm();
		if (term && term.params && term.params.length > index) {
			const param = term.params[index];
			if (param.isStatic) {
				let str = param.toLocaleString(JSG.getParserLocaleSettings());
				str = str.replace(/^"(.+(?="$))"$/, '$1');
				return str;
			} else {
				return `=${param.toString({ sheet: sheet.model, useName: true })}`;
			}
		}
		return '';
	};

	const getLocalFormula = (expr, sheet) => {
		if (!expr) {
			return '';
		}

		if (expr instanceof Array) {
			let formula = '';
			for (let i = 0; i < expr.length; i += 1) {
				const expression = expr[i];
				formula += expression ? expression.toParamString(sheet.model, -1, true) : '';
				if (i < expr.length - 1) {
					formula += JSG.getParserLocaleSettings().separators.parameter === ',' ? ';' : ',';
				}
			}
			return formula;
		}

		return expr
			? expr.toLocaleString(JSG.getParserLocaleSettings(), {
					sheet: sheet.model,
					useName: true
			  })
			: undefined;
	};

	const getDataSourceLabelByType = (serie) => {
		switch (serie.type) {
		case 'map':
			return intl.formatMessage({ id: 'StreamChartSettings.SeriesMap' }, {});
		case 'state':
			return intl.formatMessage({ id: 'StreamChartSettings.SeriesState' }, {});
		case 'heatmap':
			return intl.formatMessage({ id: 'StreamChartSettings.SeriesHeat' }, {});
		case 'bubble':
			return intl.formatMessage({ id: 'StreamChartSettings.SeriesMap' }, {});
		default:
			return intl.formatMessage({ id: 'StreamChartSettings.Series' }, {});
		}
	};

	const axis = getAxisData();
	const series = getSeriesData();
	const sheetItem = shapeSheet;
	const xyType = chart.isXYType();
	const boxPlot = chart.chart && chart.isBoxPlot();
	const gauge = chart.chart && chart.isGauge();
	const doughnut = chart.chart && chart.isDoughnut();
	const stateChart = chart.chart && isStateChart();
	const circular = chart.chart && chart.isCircular();
	const map = chart.chart && chart.isMap();
	const categories = expanded === 'panelSeries' ? chart.getCategoryLabelList(series) : [];

	return (
		<div
			style={{
				position: 'relative'
			}}
		>
			<div>
				<StreamSheetAccordion
					id="panelTitle"
					expanded={expanded === 'panelPlot'}
					onChange={() => setExpanded(expanded === 'panelPlot' ? undefined : 'panelPlot')}
					title={<FormattedMessage id="StreamChartSettings.DataSource" defaultMessage="Data Source" />}
				>
					{expanded === 'panelPlot' ? (
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										checked={chart.chart.coharentData}
										onChange={(event, state) => handleChartCoharentChange(event, state)}
									/>
								}
								label={
									<FormattedMessage
										id="StreamChartSettings.DataCoharent"
										defaultMessage="Data Range coharent"
									/>
								}
							/>
							{chart.chart.coharentData ? (
								<FormGroup>
									<TextField
										variant="outlined"
										size="small"
										margin="normal"
										fullWidth
										label={
											<FormattedMessage
												id="StreamChartSettings.ChartDataRange"
												defaultMessage="Data Range"
											/>
										}
										onBlur={(event) => handleChartFormulaBlur(event)}
										onKeyPress={(event) => {
											if (event.key === 'Enter') {
												handleChartFormulaBlur(event);
											}
										}}
										InputLabelProps={{ shrink: true }}
										InputProps={{
											inputComponent: MyInputComponent,
											inputProps: {
												component: CellRangeComponent,
												sheetView,
												value: {},
												range: chart.chart.formula.toLocaleString(
													JSG.getParserLocaleSettings(),
													{
														sheet: sheetItem.model,
														useName: true
													}
												)
											}
										}}
									/>

									{!chart.isTimeBasedChart() ? (
										<div>
											<FormControlLabel
												control={
													<Checkbox
														checked={chart.chart.dataInRows}
														onChange={(event, state) =>
															handleChartDataInRowsChange(event, state)
														}
													/>
												}
												label={
													<FormattedMessage
														id="StreamChartSettings.DataInRows"
														defaultMessage="Data in Rows"
													/>
												}
											/>
											<FormControlLabel
												control={
													<Checkbox
														checked={chart.chart.firstSeriesLabels}
														onChange={(event, state) =>
															handleChartFirstSeriesLabelsChange(event, state)
														}
													/>
												}
												label={
													chart.chart.dataInRows ? (
														<FormattedMessage
															id="StreamChartSettings.FirstSeriesLabelsRow"
															defaultMessage="First Row has Series Labels"
														/>
													) : (
														<FormattedMessage
															id="StreamChartSettings.FirstSeriesLabelsColumn"
															defaultMessage="First Column has Series Labels"
														/>
													)
												}
											/>
											<FormControlLabel
												control={
													<Checkbox
														checked={chart.chart.firstCategoryLabels}
														onChange={(event, state) =>
															handleChartFirstCategoryLabelsChange(event, state)
														}
													/>
												}
												label={
													chart.chart.dataInRows ? (
														<FormattedMessage
															id="StreamChartSettings.FirstCategoryLabelsColumn"
															defaultMessage="First Colummn has Category Labels"
														/>
													) : (
														<FormattedMessage
															id="StreamChartSettings.FirstCategoryLabelsRow"
															defaultMessage="First Row has Category Labels"
														/>
													)
												}
											/>
										</div>
									) : null}
								</FormGroup>
							) : (
								chart.series.map((serie, index) => (
									<FormGroup
										key={`s${index}`}
										style={{
											marginTop: '5px',
											borderTop: '1px solid #CCCCCC'
										}}
									>
										<FormGroup
											style={{
												margin: '0px',
												flexDirection: 'row',
												justifyContent: 'space-between'
											}}
										>
											<Typography
												color="textSecondary"
												variant="caption"
												style={{ fontSize: '0.85rem', marginTop: '4px' }}
											>
												{intl.formatMessage(
													{ id: 'StreamChartSettings.SeriesRanges' },
													{ series: getLabel(serie) }
												)}
											</Typography>
											<div>
												<IconButton
													style={{
														display: 'inline',
														padding: '4px 4px 0px 4px'
													}}
													onClick={() => handleSeriesDuplicate(index)}
													size="large"
												>
													<SvgIcon viewBox="-3 -3 32 32">
														<path d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z" />
													</SvgIcon>
												</IconButton>
												{chart.series.length > 1 ? (
													<IconButton
														style={{
															display: 'inline',
															padding: '4px 4px 0px 4px'
														}}
														onClick={() => handleSeriesDelete(index)}
														size="large"
													>
														<DeleteIcon fontSize="inherit" />
													</IconButton>
												) : null}
												{index ? (
													<IconButton
														style={{
															display: 'inline',
															padding: '4px 4px 0px 4px'
														}}
														onClick={() => handleSeriesMoveUp(index)}
														size="large"
													>
														<SvgIcon>
															<path d="M15,20H9V12H4.16L12,4.16L19.84,12H15V20Z" />
														</SvgIcon>
													</IconButton>
												) : null}
												{index < chart.series.length - 1 ? (
													<IconButton
														style={{
															display: 'inline',
															padding: '4px 4px 0px 4px'
														}}
														onClick={() => handleSeriesMoveDown(index)}
														size="large"
													>
														<SvgIcon>
															<path d="M9,4H15V12H19.84L12,19.84L4.16,12H9V4Z" />
														</SvgIcon>
													</IconButton>
												) : null}
											</div>
										</FormGroup>

										<FormControlLabel
											control={
												<Checkbox
													checked={serie.timeSeries}
													onChange={(event, state) =>
														handleSeriesTimeSeriesChange(event, serie, state)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.TimeSeries"
													defaultMessage="Time Series"
												/>
											}
										/>
										{serie.timeSeries
											? [
													<TextField
														key={`catlabel${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															<FormattedMessage
																id="StreamChartSettings.CategoriesLabel"
																defaultMessage="Category Label"
															/>
														}
														onBlur={(event) =>
															handleCategoriesLabelFormulaBlur(event, serie)
														}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleCategoriesLabelFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																onlyReference: false,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaXLabel, sheetItem)
															}
														}}
													/>,
													<TextField
														key={`serlabel${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															<FormattedMessage
																id="StreamChartSettings.SeriesLabel"
																defaultMessage="Label"
															/>
														}
														onBlur={(event) => handleSeriesLabelFormulaBlur(event, serie)}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleSeriesLabelFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																onlyReference: false,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaYLabel, sheetItem)
															}
														}}
													/>,
													<TextField
														key={`servalue${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															<FormattedMessage
																id="StreamChartSettings.SeriesValues"
																defaultMessage="Values"
															/>
														}
														onBlur={(event) =>
															handleSeriesTimeValuesFormulaBlur(event, serie)
														}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleSeriesTimeValuesFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																onlyReference: false,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaTime, sheetItem)
															}
														}}
													/>,
													<TextField
														key={`serxkey${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															<FormattedMessage
																id="StreamChartSettings.SeriesXKey"
																defaultMessage="Field to use for X Categories or X Values"
															/>
														}
														onBlur={(event) =>
															handleSeriesTimeXFieldFormulaBlur(event, serie)
														}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleSeriesTimeXFieldFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																onlyReference: false,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaTimeXKey, sheetItem)
															}
														}}
													/>,
													<TextField
														key={`serykey${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															<FormattedMessage
																id="StreamChartSettings.SeriesYKey"
																defaultMessage="Field to use for Values"
															/>
														}
														onBlur={(event) =>
															handleSeriesTimeYFieldFormulaBlur(event, serie)
														}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleSeriesTimeYFieldFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																onlyReference: false,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaTimeYKey, sheetItem)
															}
														}}
													/>
											  ]
											: [
													<TextField
														key={`serlabel${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															<FormattedMessage
																id="StreamChartSettings.SeriesLabel"
																defaultMessage="Label"
															/>
														}
														onBlur={(event) => handleSeriesLabelFormulaBlur(event, serie)}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleSeriesLabelFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																onlyReference: false,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaYLabel, sheetItem)
															}
														}}
													/>,
													xyType ? (
														<TextField
															key={`sercats${index}`}
															variant="outlined"
															fullWidth
															size="small"
															margin="normal"
															label={
																<FormattedMessage
																	id="StreamChartSettings.Categories"
																	defaultMessage="Categories"
																/>
															}
															onBlur={(event) =>
																handleSeriesXYCategoriesFormulaBlur(event, serie)
															}
															onKeyPress={(event) => {
																if (event.key === 'Enter') {
																	handleSeriesXYCategoriesFormulaBlur(event, serie);
																}
															}}
															InputLabelProps={{ shrink: true }}
															InputProps={{
																inputComponent: MyInputComponent,
																inputProps: {
																	component: CellRangeComponent,
																	onlyReference: false,
																	multiple: true,
																	sheetView,
																	value: {},
																	range: getLocalFormula(
																		serie.formulaXYCategories,
																		sheetItem
																	)
																}
															}}
														/>
													) : null,
													<TextField
														key={`serxval${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															xyType ? (
																<FormattedMessage
																	id="StreamChartSettings.SeriesXValues"
																	defaultMessage="X Values"
																/>
															) : (
																<FormattedMessage
																	id="StreamChartSettings.Categories"
																	defaultMessage="Categories"
																/>
															)
														}
														onBlur={(event) =>
															handleSeriesCategoriesFormulaBlur(event, serie)
														}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleSeriesCategoriesFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																onlyReference: false,
																multiple: true,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaXValues, sheetItem)
															}
														}}
													/>,
													<TextField
														key={`seryval${index}`}
														variant="outlined"
														fullWidth
														size="small"
														margin="normal"
														label={
															xyType ? (
																<FormattedMessage
																	id="StreamChartSettings.SeriesYValues"
																	defaultMessage="X Values"
																/>
															) : (
																<FormattedMessage
																	id="StreamChartSettings.SeriesValues"
																	defaultMessage="Categories"
																/>
															)
														}
														onBlur={(event) => handleSeriesValuesFormulaBlur(event, serie)}
														onKeyPress={(event) => {
															if (event.key === 'Enter') {
																handleSeriesValuesFormulaBlur(event, serie);
															}
														}}
														InputLabelProps={{ shrink: true }}
														InputProps={{
															inputComponent: MyInputComponent,
															inputProps: {
																component: CellRangeComponent,
																multiple: true,
																onlyReference: false,
																sheetView,
																value: {},
																range: getLocalFormula(serie.formulaYValues, sheetItem)
															}
														}}
													/>
											  ]}
										{series.timeSeries && usesCValues(serie) ? (
											<TextField
												key={`serckey${index}`}
												variant="outlined"
												fullWidth
												size="small"
												margin="normal"
												label={() => getDataSourceLabelByType(serie)}
												onBlur={(event) => handleSeriesTimeCFieldFormulaBlur(event, serie)}
												onKeyPress={(event) => {
													if (event.key === 'Enter') {
														handleSeriesTimeCFieldFormulaBlur(event, serie);
													}
												}}
												InputLabelProps={{ shrink: true }}
												InputProps={{
													inputComponent: MyInputComponent,
													inputProps: {
														component: CellRangeComponent,
														onlyReference: false,
														sheetView,
														value: {},
														range: getLocalFormula(serie.formulaTimeCKey, sheetItem)
													}
												}}
											/>
										) : null}
										{!series.timeSeries && usesCValues(serie) ? (
											<TextField
												key={`sercval${index}`}
												variant="outlined"
												fullWidth
												size="small"
												margin="normal"
												label={getDataSourceLabelByType(serie)}
												onBlur={(event) => handleSeriesExtraValuesFormulaBlur(event, serie)}
												onKeyPress={(event) => {
													if (event.key === 'Enter') {
														handleSeriesExtraValuesFormulaBlur(event, serie);
													}
												}}
												InputLabelProps={{ shrink: true }}
												InputProps={{
													inputComponent: MyInputComponent,
													inputProps: {
														component: CellRangeComponent,
														onlyReference: false,
														multiple: true,
														sheetView,
														value: {},
														range: getLocalFormula(serie.formulaCValues, sheetItem)
													}
												}}
											/>
										) : null}
									</FormGroup>
								))
							)}
						</FormGroup>
					) : null}
				</StreamSheetAccordion>
				<StreamSheetAccordion
					id="panelChart"
					expanded={expanded === 'panelChart'}
					onChange={() => setExpanded(expanded === 'panelChart' ? undefined : 'panelChart')}
					title={<FormattedMessage id="StreamChartSettings.Chart" defaultMessage="Chart" />}
				>
					{expanded === 'panelChart' ? (
						<FormGroup>
							<TextField
								size="small"
								margin="normal"
								label={<FormattedMessage id="StreamChartSettings.template" defaultMessage="Template" />}
								select
								fullWidth
								id="templates"
								value={String(chart.chart.template)}
								onChange={handleTemplateChange}
								input={<Input name="template" id="template" />}
							>
								{Object.keys(JSG.SheetPlotNode.templates).map((key) => (
									<MenuItem value={key} key={key}>
										{intl.formatMessage({ id: `StreamChartSettings.Template${key}` }, {})}
									</MenuItem>
								))}
							</TextField>
							{!circular && !gauge ? (
								<FormControl>
									<TextField
										variant="outlined"
										size="small"
										label={
											<FormattedMessage
												id="StreamChartSettings.DataHandling"
												defaultMessage="Missing Data"
											/>
										}
										margin="normal"
										select
										fullWidth
										value={chart.chart.dataMode}
										onChange={handleDataModeChange}
									>
										<MenuItem value="datazero" key={1}>
											<FormattedMessage
												id="StreamChartSettings.DataZero"
												defaultMessage="Display as zero"
											/>
										</MenuItem>
										{map
											? [
													<MenuItem value="dataignore" key={2}>
														<FormattedMessage
															id="StreamChartSettings.DataHide"
															defaultMessage="Do not display"
														/>
													</MenuItem>,
													<MenuItem value="datainterrupt" key={3}>
														<FormattedMessage
															id="StreamChartSettings.DataGrey"
															defaultMessage="Gray Area"
														/>
													</MenuItem>
											  ]
											: [
													<MenuItem value="dataignore" key={2}>
														<FormattedMessage
															id="StreamChartSettings.DataIgnore"
															defaultMessage="Do not display"
														/>
													</MenuItem>,
													<MenuItem value="datainterrupt" key={3}>
														<FormattedMessage
															id="StreamChartSettings.DataInterrupt"
															defaultMessage="Interrupt line"
														/>
													</MenuItem>
											  ]}
										{chart.xAxes[0].type === 'category' && !map ? (
											<MenuItem value="hideempty" key={4}>
												<FormattedMessage
													id="StreamChartSettings.HideEmpty"
													defaultMessage="Hide empty Categories"
												/>
											</MenuItem>
										) : null}
									</TextField>
								</FormControl>
							) : null}
							<FormControl>
								<FormGroup>
									<FormLabel
										component="legend"
										style={{
											marginTop: '7px',
											marginBottom: '7px'
										}}
									>
										<FormattedMessage id="StreamChartSettings.Visible" defaultMessage="Visible" />
									</FormLabel>
									<FormControlLabel
										control={
											<Checkbox
												checked={chart.title.visible}
												onChange={(event, state) =>
													handleVisibleChange(event, state, chart.title, 'title')
												}
											/>
										}
										label={
											<FormattedMessage id="StreamChartSettings.title" defaultMessage="Title" />
										}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={chart.legend.visible}
												onChange={(event, state) =>
													handleVisibleChange(event, state, chart.legend, 'legend')
												}
											/>
										}
										label={
											<FormattedMessage id="StreamChartSettings.Legend" defaultMessage="Legend" />
										}
									/>
									{isFunnelChart() ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={chart.chart.seriesLines.visible}
													onChange={(event, state) =>
														handleVisibleChange(
															event,
															state,
															chart.chart.seriesLines,
															'chart'
														)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.SeriesLines"
													defaultMessage="Series Lines"
												/>
											}
										/>
									) : null}
									{isLineChart() ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={chart.chart.hiLoLines.visible}
													onChange={(event, state) =>
														handleVisibleChange(
															event,
															state,
															chart.chart.hiLoLines,
															'chart'
														)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.HiLoLines"
													defaultMessage="High Low Lines"
												/>
											}
										/>
									) : null}
									{isLineChart() ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={chart.chart.upBars.visible}
													onChange={(event, state) => handleUpDownBarsChange(event, state)}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.UpDownBars"
													defaultMessage="Up Down Bars"
												/>
											}
										/>
									) : null}
									<FormControlLabel
										control={
											<Checkbox
												checked={chart.chart.tooltips}
												onChange={(event, state) => handleTooltipsChange(event, state)}
											/>
										}
										label={
											<FormattedMessage
												id="StreamChartSettings.tooltips"
												defaultMessage="Tooltips"
											/>
										}
									/>
									{circular || gauge || chart.isType('bar') || chart.isType('column') ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={chart.chart.varyByCategories}
													onChange={(event, state) =>
														handleVaryByCategoriesChange(event, state)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.VaryByCategories"
													defaultMessage="Vary Color By Category"
												/>
											}
										/>
									) : null}
									<FormControlLabel
										control={
											<Checkbox
												checked={chart.chart.menuVisible}
												onChange={(event, state) => handleMenuVisibleChange(event, state)}
											/>
										}
										label={
											<FormattedMessage
												id="StreamChartSettings.MenuVisible"
												defaultMessage="Allow Visible Series Selection"
											/>
										}
									/>
									{map ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={chart.chart.mapZoom}
													onChange={(event, state) => handleMapZoomChange(event, state)}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.AllowMapZoom"
													defaultMessage="Allow Zoom"
												/>
											}
										/>
									) : null}
								</FormGroup>
							</FormControl>
							<FormLabel
								component="legend"
								style={{
									marginTop: '7px',
									marginBottom: '7px'
								}}
							>
								<FormattedMessage id="StreamChartSettings.Settings" defaultMessage="Settings" />
							</FormLabel>
							{gauge ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={chart.chart.gaugePointer}
											onChange={(event, state) => handleChartGaugePointerChange(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="StreamChartSettings.GaugePointer"
											defaultMessage="Use Pointer for Gauge"
										/>
									}
								/>
							) : null}
							{!map && !circular && !boxPlot && !xyType ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={chart.chart.stacked}
											onChange={(event, state) => handleChartStackedChange(event, state)}
										/>
									}
									label={
										<FormattedMessage id="StreamChartSettings.Stacked" defaultMessage="Stacked" />
									}
								/>
							) : null}
							<FormControl>
								<FormGroup>
									{circular || gauge ? (
										<div>
											<div
												style={{
													flexDirection: 'row'
												}}
											>
												{!gauge && !doughnut ? (
													<TextField
														variant="outlined"
														size="small"
														id="number"
														margin="normal"
														style={{
															width: '110px'
														}}
														label={
															<FormattedMessage
																id="StreamChartSettings.Rotation"
																defaultMessage="Rotation"
															/>
														}
														InputProps={{
															inputProps: {
																min: 10,
																max: 90,
																step: 5
															},
															endAdornment: (
																<InputAdornment position="end">
																	<FormattedMessage
																		id="StreamChartSettings.Degrees"
																		defaultMessage="Degrees"
																	/>
																</InputAdornment>
															)
														}}
														error={
															rotation > Math.PI_2 ||
															rotation < JSG.MathUtils.toRadians(10)
														}
														helperText={
															rotation > Math.PI_2 ||
															rotation < JSG.MathUtils.toRadians(10) ? (
																<FormattedMessage
																	id="StreamChartSettings.InvalidAngle"
																	defaultMessage="Angle must be between 10 and 90 degrees!"
																/>
															) : (
																''
															)
														}
														value={JSG.MathUtils.toDegrees(rotation, 0)}
														onChange={(event) => handleChartRotationChange(event)}
														onBlur={(event) => handleChartRotationBlur(event)}
														type="number"
													/>
												) : null}

												{!chart.isPie() ? (
													<TextField
														variant="outlined"
														size="small"
														margin="normal"
														style={{
															width: '48%',
															marginLeft: gauge ? '0px' : '10px'
														}}
														id="number"
														label={
															<FormattedMessage
																id="StreamChartSettings.DoughnutHole"
																defaultMessage="Doughnut Hole (Percent)"
															/>
														}
														InputProps={{
															inputProps: {
																min: 1,
																max: 95,
																step: 1
															},
															endAdornment: (
																<InputAdornment position="end">%</InputAdornment>
															)
														}}
														error={hole > 0.95 || hole < 0.01}
														helperText={
															hole > 0.95 || hole < 0.01 ? (
																<FormattedMessage
																	id="StreamChartSettings.InvalidDoughnutHole"
																	defaultMessage="The hole value must be between 1 and 95 percent!"
																/>
															) : (
																''
															)
														}
														value={Math.round(hole * 100)}
														onChange={(event) => handleChartHoleChange(event)}
														onBlur={(event) => handleChartHoleBlur(event)}
														type="number"
													/>
												) : null}
											</div>
											<div
												style={{
													flexDirection: 'row'
												}}
											>
												<TextField
													variant="outlined"
													size="small"
													margin="normal"
													style={{
														width: '48%',
													}}
													id="number"
													label={
														<FormattedMessage
															id="StreamChartSettings.StartAngle"
															defaultMessage="Pie Start"
														/>
													}
													inputProps={{
														min: 0,
														max: 360,
														step: 5
													}}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<FormattedMessage
																	id="StreamChartSettings.Degrees"
																	defaultMessage="Degrees"
																/>
															</InputAdornment>
														)
													}}
													error={checkAngles(startAngle, endAngle, true) !== ''}
													helperText={checkAngles(startAngle, endAngle, true)}
													value={JSG.MathUtils.toDegrees(startAngle, 0)}
													onChange={(event) => handleChartStartAngleChange(event)}
													onBlur={(event) => handleChartStartAngleBlur(event)}
													type="number"
												/>
												<TextField
													variant="outlined"
													size="small"
													margin="normal"
													style={{
														width: '48%',
														marginLeft: '10px'
													}}
													id="number"
													label={
														<FormattedMessage
															id="StreamChartSettings.EndAngle"
															defaultMessage="Pie End"
														/>
													}
													inputProps={{
														min: 0,
														max: 540,
														step: 5
													}}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<FormattedMessage
																	id="StreamChartSettings.Degrees"
																	defaultMessage="Degrees"
																/>
															</InputAdornment>
														)
													}}
													error={checkAngles(startAngle, endAngle, false) !== ''}
													helperText={checkAngles(startAngle, endAngle, false)}
													value={JSG.MathUtils.toDegrees(endAngle, 0)}
													onChange={(event) => handleChartEndAngleChange(event)}
													onBlur={(event) => handleChartEndAngleBlur(event)}
													type="number"
												/>
											</div>
										</div>
									) : (
										<div>
											<FormGroup>
												{!boxPlot && !map && !xyType ? (
													<FormControlLabel
														control={
															<Checkbox
																checked={chart.chart.relative}
																onChange={(event, state) =>
																	handleChartHundredPercentChange(event, state)
																}
															/>
														}
														label={
															<FormattedMessage
																id="StreamChartSettings.HundredPercent"
																defaultMessage="100%"
															/>
														}
													/>
												) : null}
												{isLineChart() ? (
													<FormControlLabel
														control={
															<Checkbox
																checked={chart.chart.step}
																onChange={(event, state) =>
																	handleChartStepChange(event, state)
																}
															/>
														}
														label={
															<FormattedMessage
																id="StreamChartSettings.Step"
																defaultMessage="Step"
															/>
														}
													/>
												) : null}
												{chart.isType('bar') || chart.isType('column') ? (
													<FormControlLabel
														control={
															<Checkbox
																checked={chart.chart.segments}
																onChange={(event, state) =>
																	handleChartSegmentsChange(event, state)
																}
															/>
														}
														label={
															<FormattedMessage
																id="StreamChartSettings.Segments"
																defaultMessage="Segments"
															/>
														}
													/>
												) : null}
											</FormGroup>
										</div>
									)}
									{chart.series.length && chart.allowThreshold(chart.series[0]) && chart.series[0].type !== 'heatmap' ? (
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											label={
												<FormattedMessage
													id="StreamChartSettings.ThresholdStyle"
													defaultMessage="Treshold Style"
												/>
											}
											select
											margin="normal"
											value={chart.chart.varyByThreshold}
											onChange={handleVaryByThresholdChange}
										>
											<MenuItem value="none" key={0}>
												<FormattedMessage
													id="StreamChartSettings.NoChange"
													defaultMessage="None"
												/>
											</MenuItem>
											{chart.series[0].type !== 'area' ?
											<MenuItem value="colorchange" key={1}>
												<FormattedMessage
													id="StreamChartSettings.ColorChange"
													defaultMessage="Color Change"
												/>
											</MenuItem> : null}
											{!gauge && !stateChart && !isBubbleChart() ? (
												<MenuItem value="gradient" key={2}>
													<FormattedMessage
														id="StreamChartSettings.Gradient"
														defaultMessage="Gradient"
													/>
												</MenuItem>
											) : null}
										</TextField>
									) : null}
									{chart.series.length &&
									(chart.allowThreshold(chart.series[0]) || chart.isType('waterfall'))
										? [
												<ValueRangesDialog
													key="valuedlg"
													sheetView={sheetView}
													open={showValueRanges}
													ranges={chart.chart.valueRanges}
													chart={chart.chart}
													stateHandler={handleEditChartValueRanges}
												/>,
												<FormControl key="valuebtn" x={{ marginTop: '15px' }}>
													<DialogButton
														marginV
														onClick={() => handleEditChartValueRanges(true)}
														variant="outlined"
													>
														<FormattedMessage
															id="StreamChartSettings.EditChartValueRanges"
															defaultMessage="Edit Value Ranges"
														/>
													</DialogButton>
												</FormControl>
										  ]
										: null}
									{
										!chart.isPie() && !gauge && !map && !chart.chart.stacked ?
											<TextField
												variant="outlined"
												size="small"
												margin="normal"
												label={
													<FormattedMessage
														id="StreamChartSettings.Shapes"
														defaultMessage="Shapes"
													/>
												}
												onBlur={(event) => handleChartShapesFormulaBlur(event)}
												onKeyPress={(event) => {
													if (event.key === 'Enter') {
														handleChartShapesFormulaBlur(event);
													}
												}}
												InputLabelProps={{shrink: true}}
												InputProps={{
													inputComponent: MyInputComponent,
													inputProps: {
														component: CellRangeComponent,
														onlyReference: false,
														sheetView,
														value: {},
														range: getLocalFormula(chart.chart.shapesJson, sheetItem)
													}
												}}
											/> : null
									}
								</FormGroup>
							</FormControl>
						</FormGroup>
					) : null}
				</StreamSheetAccordion>
				<StreamSheetAccordion
					id="panelAxes"
					expanded={expanded === 'panelAxes'}
					onChange={() => setExpanded(expanded === 'panelAxes' ? undefined : 'panelAxes')}
					title={<FormattedMessage id="StreamChartSettings.Axes" defaultMessage="Axes" />}
				>
					{expanded === 'panelAxes' ? (
						<FormGroup>
							<ValueRangesDialog
								sheetView={sheetView}
								open={showValueRanges}
								ranges={axis.valueRanges}
								stateHandler={handleEditValueRanges}
							/>
							<TextField
								variant="outlined"
								size="small"
								label={<FormattedMessage id="StreamChartSettings.Axes" defaultMessage="Axes" />}
								select
								margin="normal"
								value={axisType === 'xAxis' ? axisIndex : chart.xAxes.length + axisIndex}
								onChange={handleAxesIndexChange}
							>
								{!gauge
									? chart.xAxes.map((axs, index) => (
											<MenuItem value={index} key={axs.name}>
												{axs.name}
											</MenuItem>
									  ))
									: null}
								{chart.yAxes.map((axs, index) => (
									<MenuItem value={chart.xAxes.length + index} key={axs.name}>
										{axs.name}
									</MenuItem>
								))}
							</TextField>
							<TextField
								variant="outlined"
								size="small"
								margin="normal"
								label={
									<FormattedMessage id="StreamChartSettings.axisType" defaultMessage="Axis Type" />
								}
								select
								fullWidth
								value={axis.type}
								onChange={handleAxisTypeChange}
							>
								{axisType === 'xAxis' ? (
									<MenuItem value="category" key={0}>
										<FormattedMessage
											id="StreamChartSettings.axisTypeCategory"
											defaultMessage="Category"
										/>
									</MenuItem>
								) : null}
								<MenuItem value="linear" key={1}>
									<FormattedMessage id="StreamChartSettings.axisTypeLinear" defaultMessage="Linear" />
								</MenuItem>
								{gauge === false
									? [
											<MenuItem value="time" key={2}>
												<FormattedMessage
													id="StreamChartSettings.axisTypeTime"
													defaultMessage="Time"
												/>
											</MenuItem>,
											<MenuItem value="logarithmic" key={3}>
												<FormattedMessage
													id="StreamChartSettings.axisTypeLogarithmic"
													defaultMessage="Logarithmic"
												/>
											</MenuItem>
									  ]
									: null}
							</TextField>
							<TextField
								variant="outlined"
								fullWidth
								size="small"
								margin="normal"
								label={
									<FormattedMessage id="StreamChartSettings.AxisMinimum" defaultMessage="Minimum" />
								}
								onBlur={(event) => handleAxisMinimumFormulaBlur(event)}
								onKeyPress={(event) => {
									if (event.key === 'Enter') {
										handleAxisMinimumFormulaBlur(event);
									}
								}}
								value={getFormulaParameter(axis.formula, 0, sheetItem)}
								InputLabelProps={{ shrink: true }}
								InputProps={{
									inputComponent: MyInputComponent,
									inputProps: {
										component: CellRangeComponent,
										sheetView,
										value: {},
										range: getFormulaParameter(axis.formula, 0, sheetItem)
									}
								}}
							/>
							<TextField
								variant="outlined"
								size="small"
								fullWidth
								margin="normal"
								label={
									<FormattedMessage id="StreamChartSettings.AxisMaximum" defaultMessage="Maximum" />
								}
								onBlur={(event) => handleAxisMaximumFormulaBlur(event)}
								onKeyPress={(event) => {
									if (event.key === 'Enter') {
										handleAxisMaximumFormulaBlur(event);
									}
								}}
								value={getFormulaParameter(axis.formula, 1, sheetItem)}
								InputLabelProps={{ shrink: true }}
								InputProps={{
									inputComponent: MyInputComponent,
									inputProps: {
										component: CellRangeComponent,
										sheetView,
										value: {},
										range: getFormulaParameter(axis.formula, 1, sheetItem)
									}
								}}
							/>
							{axis.type !== 'logarithmic' ?
							<TextField
								variant="outlined"
								size="small"
								margin="normal"
								label={<FormattedMessage id="StreamChartSettings.AxisStep" defaultMessage="Step" />}
								onBlur={(event) => handleAxisStepFormulaBlur(event)}
								onKeyPress={(event) => {
									if (event.key === 'Enter') {
										handleAxisStepFormulaBlur(event);
									}
								}}
								value={getFormulaParameter(axis.formula, 2, sheetItem)}
								InputLabelProps={{ shrink: true }}
								InputProps={{
									inputComponent: MyInputComponent,
									inputProps: {
										component: CellRangeComponent,
										sheetView,
										value: {},
										range: getFormulaParameter(axis.formula, 2, sheetItem)
									}
								}}
							/> : null}
							{axis.type === 'time' ? (
								<TextField
									variant="outlined"
									size="small"
									margin="normal"
									label={
										<FormattedMessage
											id="StreamChartSettings.AxisTimeStep"
											defaultMessage="Time Step"
										/>
									}
									onBlur={(event) => handleAxisTimeStepFormulaBlur(event)}
									onKeyPress={(event) => {
										if (event.key === 'Enter') {
											handleAxisTimeStepFormulaBlur(event);
										}
									}}
									value={getFormulaParameter(axis.formula, 3, sheetItem)}
									InputLabelProps={{
										shrink: true
									}}
									InputProps={{
										inputComponent: MyInputComponent,
										inputProps: {
											component: CellRangeComponent,
											inputEditorType: 'string',
											inputEditorOptions: [
												{ value: 'year', label: 'StreamChartSettings.Year' },
												{ value: 'quarter', label: 'StreamChartSettings.Quarter' },
												{ value: 'month', label: 'StreamChartSettings.Month' },
												{ value: 'week', label: 'StreamChartSettings.Week' },
												{ value: 'day', label: 'StreamChartSettings.Day' },
												{ value: 'hour', label: 'StreamChartSettings.Hour' },
												{ value: 'minute', label: 'StreamChartSettings.Minute' },
												{ value: 'second', label: 'StreamChartSettings.Second' },
												{ value: 'millisecond', label: 'StreamChartSettings.Millisecond' }
											],
											sheetView,
											value: {},
											range: getFormulaParameter(axis.formula, 3, sheetItem)
										}
									}}
								/>
							) : null}
							<TextField
								variant="outlined"
								size="small"
								label={
									<FormattedMessage id="StreamChartSettings.AxisPosition" defaultMessage="Position" />
								}
								select
								margin="normal"
								fullWidth
								id="axis-position"
								value={axis.align}
								onChange={handleAxisAlignChange}
							>
								{getAxisAlignOptions()}
							</TextField>
							<TextField
								variant="outlined"
								fullWidth
								size="small"
								id="number"
								label={
									<FormattedMessage
										id="StreamChartSettings.AxisLabelRotation"
										defaultMessage="Rotate Labels"
									/>
								}
								inputProps={{
									min: -90,
									max: 90,
									step: 5
								}}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<FormattedMessage
												id="StreamChartSettings.Degrees"
												defaultMessage="Degrees"
											/>
										</InputAdornment>
									)
								}}
								value={axis.format.fontRotation === undefined ? 0 : axis.format.fontRotation}
								error={axis.format.fontRotation > 90 || axis.format.fontRotation < -90}
								helperText={
									axis.format.fontRotation > 90 || axis.format.fontRotation < -90 ? (
										<FormattedMessage
											id="StreamChartSettings.InvalidAngleAxis"
											defaultMessage="Angle must be between -90 and 90 degrees!"
										/>
									) : (
										''
									)
								}
								onChange={(event) => handleAxisLabelRotationChange(event)}
								type="number"
								margin="normal"
							/>
							<TextField
								variant="outlined"
								fullWidth
								size="small"
								id="number"
								value={axis.labelDistance}
								label={
									<FormattedMessage
										id="StreamChartSettings.AxisLabelDistance"
										defaultMessage="Distance from Label to Axis"
									/>
								}
								inputProps={{
									min: 0,
									max: 10000,
									step: 50
								}}
								onChange={(event) => handleAxisLabelDistanceChange(event)}
								type="number"
								margin="normal"
							/>
							<FormControl>
								<FormGroup>
									<FormControlLabel
										control={
											<Checkbox
												checked={axis.visible}
												onChange={(event, state) => handleAxisVisibleChange(event, state)}
											/>
										}
										label={<FormattedMessage id="StreamChartSettings.Axis" defaultMessage="Axis" />}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={axis.gridVisible}
												onChange={(event, state) => handleGridlineVisibleChange(event, state)}
											/>
										}
										label={
											<FormattedMessage id="StreamChartSettings.Grid" defaultMessage="Gridline" />
										}
									/>
									{!gauge && !circular && !map ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={axis.title.visible}
													onChange={(event, state) =>
														handleAxisTitleVisibleChange(event, state)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.AxisTitle"
													defaultMessage="Axis Title"
												/>
											}
										/>
									) : null}
									{!gauge && !circular && !map && axis.title.visible ? (
										<TextField
											variant="outlined"
											sx={{ marginLeft: '26px', width: 'calc(100% - 26px)' }}
											size="small"
											margin="normal"
											label={
												<FormattedMessage
													id="StreamChartSettings.AxisTitle"
													defaultMessage="AxisTitle"
												/>
											}
											onBlur={(event) => handleAxisTitleFormulaBlur(event)}
											onKeyPress={(event) => {
												if (event.key === 'Enter') {
													handleAxisTitleFormulaBlur(event);
												}
											}}
											InputLabelProps={{ shrink: true }}
											InputProps={{
												inputComponent: MyInputComponent,
												inputProps: {
													component: CellRangeComponent,
													onlyReference: false,
													sheetView,
													value: {},
													range: getLocalFormula(axis.title.formula, sheetItem)
												}
											}}
										/>
									) : null}
									<FormControlLabel
										control={
											<Checkbox
												checked={axis.valueRangesVisible}
												onChange={(event, state) =>
													handleValueRangesVisibleChange(event, state)
												}
											/>
										}
										label={
											<FormattedMessage
												id="StreamChartSettings.ValueRanges"
												defaultMessage="Value Ranges"
											/>
										}
									/>
									<FormControl>
										<DialogButton
											marginV
											onClick={() => handleEditValueRanges(true)}
											variant="outlined"
										>
											<FormattedMessage
												id="StreamChartSettings.EditValueRanges"
												defaultMessage="Edit Value Ranges"
											/>
										</DialogButton>
									</FormControl>
									<FormLabel
										component="legend"
										style={{
											marginTop: '12px',
											marginBottom: '7px'
										}}
									>
										<FormattedMessage id="StreamChartSettings.Settings" defaultMessage="Settings" />
									</FormLabel>
									<FormControlLabel
										control={
											<Checkbox
												checked={axis.invert}
												onChange={(event, state) => handleAxisInvertChange(event, state)}
											/>
										}
										label={
											<FormattedMessage id="StreamChartSettings.Invert" defaultMessage="Invert" />
										}
									/>
									{axis.type === 'category' || axis.type === 'time'
										? null
										: [
												<FormControlLabel
													control={
														<Checkbox
															checked={axis.autoZero}
															onChange={(event, state) =>
																handleAxisAutoZeroChange(event, state)
															}
														/>
													}
													label={
														<FormattedMessage
															id="StreamChartSettings.AutoZero"
															defaultMessage="Start at Zero automatically"
														/>
													}
												/>,
												<FormControlLabel
													control={
														<Checkbox
															checked={axis.valueMinMax}
															onChange={(event, state) =>
																handleAxisValueMinMaxChange(event, state)
															}
														/>
													}
													label={
														<FormattedMessage
															id="StreamChartSettings.ValueMinMax"
															defaultMessage="Scale Axis to exact Values"
														/>
													}
												/>
										  ]}
									<FormControlLabel
										control={
											<Checkbox
												checked={axis.format.linkNumberFormat}
												onChange={(event, state) =>
													handleAxisNumberformatLinkChange(event, state)
												}
											/>
										}
										label={
											<FormattedMessage
												id="StreamChartSettings.LinkNumberFormat"
												defaultMessage="Link Numberformat to Cell"
											/>
										}
									/>
									{!map && !circular && chart.xAxes[0].name === axis.name && axis.align === 'bottom' && axis.type === 'time' ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={axis.updateZoom}
													onChange={(event, state) =>
														handleAxisUpdateZoomChange(event, state)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.UpdateZoom"
													defaultMessage="Update Zoom by ellapsing time"
												/>
											}
										/>
									) : null}
									{!map && !circular && chart.xAxes[0].name === axis.name && axis.align === 'bottom' ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={axis.allowZoom}
													onChange={(event, state) => handleAxisAllowZoomChange(event, state)}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.AllowZoom"
													defaultMessage="Allow to zoom by mouse"
												/>
											}
										/>
									) : null}
									{!map && !circular && chart.xAxes[0].name === axis.name && axis.align === 'bottom' ? (
										<TextField
											variant="outlined"
											fullWidth
											size="small"
											label={
												<FormattedMessage
													id="StreamChartSettings.ZoomGroup"
													defaultMessage="Zoom Group"
												/>
											}
											value={axis.zoomGroup}
											onBlur={(event) => handleAxisZoomGroupChange(event)}
											onChange={(event) => handleAxisZoomGroupChange(event)}
											margin="normal"
										/>
									) : null}
								</FormGroup>
							</FormControl>
							<FormControl>
								<DialogButton marginV onClick={handleAddAxis} variant="outlined">
									<FormattedMessage id="StreamChartSettings.AddAxis" defaultMessage="Add Axis" />
								</DialogButton>
							</FormControl>
							{
								isAxisDeletable(axis) ?
									<FormControl>
										<DialogButton marginV onClick={() => handleDeleteAxis(axis)} variant="outlined">
											<FormattedMessage id="StreamChartSettings.DeleteAxis" defaultMessage="Delete Axis" />
										</DialogButton>
									</FormControl> : null
							}
						</FormGroup>
					) : null}
				</StreamSheetAccordion>
				<StreamSheetAccordion
					id="panelSeries"
					expanded={expanded === 'panelSeries'}
					onChange={() => setExpanded(expanded === 'panelSeries' ? undefined : 'panelSeries')}
					title={<FormattedMessage id="StreamChartSettings.Serie" defaultMessage="Series" />}
				>
					<FormGroup>
						<TextField
							variant="outlined"
							size="small"
							label={<FormattedMessage id="StreamChartSettings.Series" defaultMessage="Series" />}
							select
							fullWidth
							margin="normal"
							value={chart.series.indexOf(series)}
							onChange={handleSeriesIndexChange}
						>
							{chart.series.map((serie, index) => {
								const label = getLabel(serie);

								return (
									<MenuItem value={index} key={label}>
										{label}
									</MenuItem>
								);
							})}
						</TextField>
						<TextField
							variant="outlined"
							size="small"
							label={<FormattedMessage id="StreamChartSettings.Point" defaultMessage="Data Points" />}
							select
							fullWidth
							margin="normal"
							value={pointIndex}
							onChange={handlePointIndexChange}
						>
							<MenuItem value={-1} key="p-1">
								<FormattedMessage id="StreamChartSettings.PointAll" defaultMessage="All" />
							</MenuItem>
							{categories.map((label, index) => {
								return (
									// eslint-disable-next-line react/no-array-index-key
									<MenuItem value={index} key={`p${index}`}>
										{label}
									</MenuItem>
								);
							})}
						</TextField>
						{pointIndex === -1 && (series.type === 'bar' || series.type === 'profile') ? (
							<TextField
								fullWidth
								variant="outlined"
								size="small"
								margin="normal"
								label={<FormattedMessage id="StreamChart.Type" defaultMessage="Series Display Type" />}
								select
								value={series.type}
								onChange={handleSeriesTypeChange}
							>
								<MenuItem value="profile" key={1}>
									<FormattedMessage id="StreamChart.Profile" defaultMessage="Profile" />
								</MenuItem>
								<MenuItem value="bar" key={2}>
									<FormattedMessage id="StreamChart.Bar" defaultMessage="Bar" />
								</MenuItem>
							</TextField>
						) : null}
						{pointIndex === -1 &&
						(series.type === 'line' || series.type === 'column' || series.type === 'area') ? (
							<TextField
								variant="outlined"
								size="small"
								label={<FormattedMessage id="StreamChart.Type" defaultMessage="Series Display Type" />}
								select
								margin="normal"
								fullWidth
								value={series.type}
								onChange={handleSeriesTypeChange}
							>
								<MenuItem value="line" key={1}>
									<FormattedMessage id="StreamChart.Line" defaultMessage="Line" />
								</MenuItem>
								<MenuItem value="column" key={2}>
									<FormattedMessage id="StreamChart.Column" defaultMessage="Column" />
								</MenuItem>
								<MenuItem value="area" key={3}>
									<FormattedMessage id="StreamChart.Area" defaultMessage="Area" />
								</MenuItem>
							</TextField>
						) : null}
						{pointIndex === -1 && gauge && chart.chart.gaugePointer
							? [
									<TextField
										variant="outlined"
										size="small"
										fullWidth
										label={
											<FormattedMessage
												id="StreamChartSettings.GaugePointerType"
												defaultMessage="Gauge Pointer Type"
											/>
										}
										select
										margin="normal"
										value={series.pointerType}
										onChange={handleSeriesPointerTypeChange}
									>
										<MenuItem value="none" key={0}>
											<FormattedMessage id="StreamChartSettings.None" defaultMessage="None" />
										</MenuItem>
										<MenuItem value="line" key={1}>
											<FormattedMessage id="StreamChartSettings.Line" defaultMessage="Line" />
										</MenuItem>
										<MenuItem value="narrowingline" key={2}>
											<FormattedMessage
												id="StreamChartSettings.NarrowingLine"
												defaultMessage="Narrowing Line"
											/>
										</MenuItem>
										<MenuItem value="arrow" key={3}>
											<FormattedMessage id="StreamChartSettings.Arrow" defaultMessage="Arrow" />
										</MenuItem>
									</TextField>,
									<TextField
										variant="outlined"
										size="small"
										fullWidth
										label={
											<FormattedMessage
												id="StreamChartSettings.GaugePointerLength"
												defaultMessage="Gauge Pointer length"
											/>
										}
										select
										margin="normal"
										value={series.pointerLength}
										onChange={handleSeriesPointerLengthChange}
									>
										<MenuItem value="inner" key={0}>
											<FormattedMessage
												id="StreamChartSettings.InnerRadius"
												defaultMessage="Inner"
											/>
										</MenuItem>
										<MenuItem value="center" key={1}>
											<FormattedMessage
												id="StreamChartSettings.CenterRadius"
												defaultMessage="Center"
											/>
										</MenuItem>
										<MenuItem value="outer" key={2}>
											<FormattedMessage
												id="StreamChartSettings.OuterRadius"
												defaultMessage="Outer"
											/>
										</MenuItem>
									</TextField>
							  ]
							: null}
						{chart.isLineType(series) || map ? (
							<div style={{ display: 'grid' }}>
								<FormLabel style={{ marginTop: '8px' }}>
									<FormattedMessage id="StreamChartSettings.Marker" defaultMessage="Marker" />
								</FormLabel>
								<div
									style={{
										display: 'inline-flex'
									}}
								>
									<FormControl
										style={{
											width: '50%',
											marginRight: '10px'
										}}
									>
										<TextField
											variant="outlined"
											size="small"
											label={
												<FormattedMessage
													id="StreamChartSettings.Style"
													defaultMessage="Style"
												/>
											}
											select
											margin="normal"
											id="series-marker"
											value={getMarkerStyle(series)}
											onChange={handleSeriesMarkerStyleChange}
											input={<Input name="series-marker" id="series-marker" />}
										>
											<MenuItem value="none" key={1}>
												<FormattedMessage id="StreamChartSettings.none" defaultMessage="None" />
											</MenuItem>
											<MenuItem value="circle" key={2}>
												<FormattedMessage
													id="StreamChartSettings.circle"
													defaultMessage="Circle"
												/>
											</MenuItem>
											<MenuItem value="cross" key={3}>
												<FormattedMessage
													id="StreamChartSettings.cross"
													defaultMessage="Cross"
												/>
											</MenuItem>
											<MenuItem value="crossRot" key={4}>
												<FormattedMessage
													id="StreamChartSettings.rotatedCross"
													defaultMessage="Rotated Cross"
												/>
											</MenuItem>
											<MenuItem value="dash" key={5}>
												<FormattedMessage id="StreamChartSettings.dash" defaultMessage="Dash" />
											</MenuItem>
											<MenuItem value="dashright" key={6}>
												<FormattedMessage
													id="StreamChartSettings.dashright"
													defaultMessage="Dash Right"
												/>
											</MenuItem>
											<MenuItem value="line" key={7}>
												<FormattedMessage id="StreamChartSettings.line" defaultMessage="Line" />
											</MenuItem>
											<MenuItem value="rect" key={8}>
												<FormattedMessage
													id="StreamChartSettings.rectangle"
													defaultMessage="Rectangle"
												/>
											</MenuItem>
											<MenuItem value="rectRot" key={9}>
												<FormattedMessage
													id="StreamChartSettings.diamond"
													defaultMessage="Diamond"
												/>
											</MenuItem>
											<MenuItem value="star" key={10}>
												<FormattedMessage id="StreamChartSettings.star" defaultMessage="Star" />
											</MenuItem>
											<MenuItem value="triangle" key={11}>
												<FormattedMessage
													id="StreamChartSettings.triangle"
													defaultMessage="Triangle"
												/>
											</MenuItem>
											<MenuItem value="vertical" key={12}>
												<FormattedMessage
													id="StreamChartSettings.vertical"
													defaultMessage="Vertical Line"
												/>
											</MenuItem>
										</TextField>
									</FormControl>
									<FormControl
										style={{
											width: '50%'
										}}
									>
										<TextField
											variant="outlined"
											size="small"
											label={
												<FormattedMessage
													id="StreamChartSettings.MarkerSize"
													defaultMessage="Size"
												/>
											}
											margin="normal"
											select
											value={getMarkerSize(series)}
											onChange={handleSeriesMarkerSizeChange}
											input={<Input name="template" id="template" />}
										>
											{Object.values(markerSizes).map((key) => (
												<MenuItem value={key} key={key}>
													{key}
												</MenuItem>
											))}
										</TextField>
									</FormControl>
								</div>
								{!map ? (
									<div
										style={{
											display: 'inline-flex',
											flexDirection: 'row'
										}}
									>
										<FormControl
											size="small"
											variant="outlined"
											margin="normal"
											style={{
												width: '50%',
												marginRight: '10px'
											}}
										>
											<ColorComponent
												label={
													<FormattedMessage
														id="StreamChartSettings.FillColor"
														defaultMessage="Fill Color"
													/>
												}
												labelFontSize="1rem"
												transparent
												color={getMarkerFillColor(series)}
												onChange={(color) => handleSeriesMarkerFillColorChange(color)}
											/>
										</FormControl>
										<FormControl
											size="small"
											variant="outlined"
											margin="normal"
											style={{
												display: 'inline-flex',
												width: '50%'
											}}
										>
											<ColorComponent
												labelFontSize="1rem"
												label={
													<FormattedMessage
														id="StreamChartSettings.LineColor"
														defaultMessage="Line Color"
													/>
												}
												transparent
												color={getMarkerLineColor(series)}
												onChange={(color) => handleSeriesMarkerLineColorChange(color)}
											/>
										</FormControl>
									</div>
								) : null}
							</div>
						) : null}
						{pointIndex === -1 && (chart.xAxes.length > 1 || chart.yAxes.length > 1) ? (
							<FormLabel
								component="legend"
								style={{
									marginTop: '12px'
								}}
							>
								<FormattedMessage
									id="StreamChartSettings.AxisAssignment"
									defaultMessage="Axis Assignment"
								/>
							</FormLabel>
						) : null}
						{pointIndex === -1 ? (
							<div
								style={{
									display: 'inline-flex',
									flexDirection: 'row'
								}}
							>
								{chart.xAxes.length > 1 ? (
									<FormControl
										style={{
											width: '50%',
											marginRight: '10px'
										}}
									>
										<TextField
											variant="outlined"
											size="small"
											label={
												<FormattedMessage
													id="StreamChartSettings.XAxis"
													defaultMessage="XAxis"
												/>
											}
											select
											margin="normal"
											id="templates"
											value={series.xAxis}
											onChange={handleSeriesXAxisChange}
											input={<Input name="template" id="template" />}
										>
											{chart.xAxes.map((axs) => (
												<MenuItem value={axs.name} key={axs.name}>
													{axs.name}
												</MenuItem>
											))}
										</TextField>
									</FormControl>
								) : null}
								{chart.yAxes.length > 1 ? (
									<FormControl
										style={{
											width: '50%'
										}}
									>
										<TextField
											variant="outlined"
											size="small"
											label={
												<FormattedMessage
													id="StreamChartSettings.YAxis"
													defaultMessage="YAxis"
												/>
											}
											select
											margin="normal"
											id="templates"
											value={series.yAxis}
											onChange={handleSeriesYAxisChange}
											input={<Input name="template" id="template" />}
										>
											{chart.yAxes.map((axs) => (
												<MenuItem value={axs.name} key={axs.name}>
													{axs.name}
												</MenuItem>
											))}
										</TextField>
									</FormControl>
								) : null}
							</div>
						) : null}
						<FormGroup>
							{pointIndex === -1 && chart.isLineType(series) ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={series.smooth}
											onChange={(event, state) => handleSeriesSmoothChange(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="StreamChartSettings.SmoothLine"
											defaultMessage="Smooth Line"
										/>
									}
								/>
							) : null}
							{pointIndex === -1 && series.type === 'boxplot' ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={series.innerPoints}
											onChange={(event, state) => handleSeriesInnerPointsChange(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="StreamChartSettings.InnerPoints"
											defaultMessage="Inner Points"
										/>
									}
								/>
							) : null}
							{pointIndex === -1 && series.type === 'map' ? (
								<React.Fragment>
									<TextField
										variant="outlined"
										size="small"
										fullWidth
										label={
											<FormattedMessage
												id="StreamChartSettings.MapName"
												defaultMessage="Map Name"
											/>
										}
										select
										margin="normal"
										value={series.map.name}
										onChange={handleSeriesMapName}
									>
										{JSG.mapInfo.maps.map((info) => (
											<MenuItem value={info.file} key={info.id}>
												{info.name}
											</MenuItem>
										))}
										<MenuItem value="sheet" key={4}>
											<FormattedMessage
												id="StreamChartSettings.MapCustom"
												defaultMessage="From Sheet"
											/>
										</MenuItem>
									</TextField>
									<TextField
										variant="outlined"
										fullWidth
										size="small"
										select
										label={
											<FormattedMessage
												id="StreamChartSettings.CategoryName"
												defaultMessage="Property for Labels"
											/>
										}
										defaultValue={series.map.label}
										onChange={(event) => handleSeriesMapLabelChange(event)}
										margin="normal"
									>
										{series.map && series.map.mapData && series.map.mapData.features.length ? (
											Object.keys(series.map.mapData.features[0].properties).map((prop) => (
												<MenuItem value={prop} key={prop}>
													{prop}
												</MenuItem>
											))
										) : (
											<MenuItem value="name" key="name">
												name
											</MenuItem>
										)}
									</TextField>
									<TextField
										variant="outlined"
										size="small"
										fullWidth
										label={
											<FormattedMessage
												id="StreamChartSettings.MapDisplayValues"
												defaultMessage="Display Values"
											/>
										}
										select
										SelectProps={{
											multiple: true,
											renderValue: (selected) => {
												let result = '';
												selected.forEach((sel, index) => {
													result += intl.formatMessage(
														{ id: `StreamChartSettings.MapDisplay${sel}` },
														{}
													);
													if (index < selected.length - 1) {
														result += ', ';
													}
												});
												return result;
											}
										}}
										margin="normal"
										value={series.map.displayType}
										onChange={handleSeriesMapDisplayType}
									>
										<MenuItem value="color" key={0}>
											<Checkbox checked={series.map.displayType.indexOf('color') !== -1} />
											<FormattedMessage
												id="StreamChartSettings.MapDisplaycolor"
												defaultMessage="Color Intensity"
											/>
										</MenuItem>
										<MenuItem value="cvalue" key={4}>
											<Checkbox checked={series.map.displayType.indexOf('cvalue') !== -1} />
											<FormattedMessage
												id="StreamChartSettings.MapDisplaycvalue"
												defaultMessage="Color Value"
											/>
										</MenuItem>
										<MenuItem value="line" key={1}>
											<Checkbox checked={series.map.displayType.indexOf('line') !== -1} />
											<FormattedMessage
												id="StreamChartSettings.MapDisplayline"
												defaultMessage="Line Width"
											/>
										</MenuItem>
										<MenuItem value="radius" key={2}>
											<Checkbox checked={series.map.displayType.indexOf('radius') !== -1} />
											<FormattedMessage
												id="StreamChartSettings.MapDisplayradius"
												defaultMessage="Point Radius"
											/>
										</MenuItem>
										<MenuItem value="chart" key={3}>
											<Checkbox checked={series.map.displayType.indexOf('chart') !== -1} />
											<FormattedMessage
												id="StreamChartSettings.MapDisplaychart"
												defaultMessage="Chart"
											/>
										</MenuItem>
									</TextField>
									{series.map.displayType.indexOf('chart') !== -1 ? (
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											label={
												<FormattedMessage
													id="StreamChartSettings.MapChartType"
													defaultMessage="Chart Type"
												/>
											}
											select
											margin="normal"
											value={series.map.chartType}
											onChange={handleSeriesMapChartType}
										>
											<MenuItem value="pie" key={0}>
												<FormattedMessage id="StreamChart.Pie" defaultMessage="Pie" />
											</MenuItem>
											<MenuItem value="doughnut" key={1}>
												<FormattedMessage id="StreamChart.Doughnut" defaultMessage="Doughnut" />
											</MenuItem>
											<MenuItem value="column" key={2}>
												<FormattedMessage id="StreamChart.Column" defaultMessage="Column" />
											</MenuItem>
											<MenuItem value="line" key={3}>
												<FormattedMessage id="StreamChart.Line" defaultMessage="Line" />
											</MenuItem>
											<MenuItem value="area" key={4}>
												<FormattedMessage id="StreamChart.Area" defaultMessage="Area" />
											</MenuItem>
										</TextField>
									) : null}
									<FormControl>
										<DialogButton
											marginV
											disabled={series.map.name === 'sheet'}
											onClick={handleSeriesCopyMapLabels}
										>
											<FormattedMessage
												id="StreamChartSettings.CopyMapLabels"
												defaultMessage="Copy Map Labels"
											/>
										</DialogButton>
									</FormControl>
								</React.Fragment>
							) : null}
							{pointIndex === -1 && series.type === 'boxplot' ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={series.outerPoints}
											onChange={(event, state) => handleSeriesOuterPointsChange(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="StreamChartSettings.OuterPoints"
											defaultMessage="Outer Points"
										/>
									}
								/>
							) : null}
							{pointIndex === -1 && series.type === 'boxplot' ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={series.average}
											onChange={(event, state) => handleSeriesAverageChange(event, state)}
										/>
									}
									label={
										<FormattedMessage id="StreamChartSettings.Average" defaultMessage="Average" />
									}
								/>
							) : null}
							{pointIndex === -1 && series.type === 'waterfall' ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={series.autoSum}
											onChange={(event, state) => handleSeriesAutoSumChange(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="StreamChartSettings.AutoSum"
											defaultMessage="Display Sum and Difference automatically"
										/>
									}
								/>
							) : null}
							{pointIndex !== -1 && series.type === 'waterfall' ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={getPointSum(series)}
											onChange={(event, state) => handlePointSumChange(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="StreamChartSettings.PointSum"
											defaultMessage="Display Sum"
										/>
									}
								/>
							) : null}
							{pointIndex === -1 ? (
								<FormControlLabel
									control={
										<Checkbox
											checked={series.visible}
											onChange={(event, state) => handleSeriesVisibleChange(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="StreamChartSettings.SeriesVisible"
											defaultMessage="Series Visible"
										/>
									}
								/>
							) : null}
							<FormControlLabel
								control={
									<Checkbox
										checked={getDataLabelVisible(series)}
										onChange={(event, state) => handleSeriesDataLabelsChange(event, state)}
									/>
								}
								label={
									<FormattedMessage
										id="StreamChartSettings.ShowDataLabels"
										defaultMessage="Show Data Labels"
									/>
								}
							/>
							{getDataLabelVisible(series) ? (
								<FormGroup sx={{ marginLeft: '22px' }}>
									{xyType ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={getDataLabelContent(series, 'cat')}
													onChange={(event, state) =>
														handleSeriesDataLabelsContentChange(event, 'cat', state)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.Category"
													defaultMessage="Category"
												/>
											}
										/>
									) : null}
									<FormControlLabel
										control={
											<Checkbox
												checked={getDataLabelContent(series, 'x')}
												onChange={(event, state) =>
													handleSeriesDataLabelsContentChange(event, 'x', state)
												}
											/>
										}
										label={
											xyType ? (
												<FormattedMessage
													id="StreamChartSettings.XValues"
													defaultMessage="X Values"
												/>
											) : (
												<FormattedMessage
													id="StreamChartSettings.Category"
													defaultMessage="Category"
												/>
											)
										}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={getDataLabelContent(series, 'y')}
												onChange={(event, state) =>
													handleSeriesDataLabelsContentChange(event, 'y', state)
												}
											/>
										}
										label={
											<FormattedMessage
												id="StreamChartSettings.YValues"
												defaultMessage="Y Values"
											/>
										}
									/>
									{series.type === 'bubble' ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={getDataLabelContent(series, 'radius')}
													onChange={(event, state) =>
														handleSeriesDataLabelsContentChange(event, 'radius', state)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.Radius"
													defaultMessage="Radius"
												/>
											}
										/>
									) : null}
									{series.type === 'state' ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={getDataLabelContent(series, 'state')}
													onChange={(event, state) =>
														handleSeriesDataLabelsContentChange(event, 'state', state)
													}
												/>
											}
											label={
												<FormattedMessage
													id="StreamChartSettings.State"
													defaultMessage="State"
												/>
											}
										/>
									) : null}
									{series.type === 'boxplot' ? (
										<FormControlLabel
											control={
												<Checkbox
													checked={series.dataLabel.content.id}
													onChange={(event, state) =>
														handleSeriesDataLabelsContentChange(event, 'id', state)
													}
												/>
											}
											label={<FormattedMessage id="StreamChartSettings.ID" defaultMessage="ID" />}
										/>
									) : null}
									<FormControlLabel
										control={
											<Checkbox
												checked={series.dataLabel.content.series}
												onChange={(event, state) =>
													handleSeriesDataLabelsContentChange(event, 'series', state)
												}
											/>
										}
										label={
											<FormattedMessage id="StreamChartSettings.Series" defaultMessage="Series" />
										}
									/>
									<TextField
										variant="outlined"
										size="small"
										label={
											<FormattedMessage
												id="StreamChartSettings.Position"
												defaultMessage="Position"
											/>
										}
										fullWidth
										margin="normal"
										select
										id="position"
										value={getDataLabelPosition(series)}
										onChange={handleSeriesDataLabelsPositionChange}
										input={<Input name="position" id="position" />}
									>
										{getLabelPositionOptions(series)}
									</TextField>
									<TextField
										variant="outlined"
										size="small"
										fullWidth
										margin="normal"
										label={
											<FormattedMessage
												id="StreamChartSettings.Separator"
												defaultMessage="Separator"
											/>
										}
										select
										id="hide-empty"
										value={getDataLabelSeparator(series)}
										onChange={handleSeriesDataLabelsSeparatorChange}
										input={<Input name="hide-empty" id="hide-empty" />}
									>
										<MenuItem value="&lf" key={1}>
											<FormattedMessage
												id="StreamChartSettings.LineFeed"
												defaultMessage="Line Feed"
											/>
										</MenuItem>
										<MenuItem value=" " key={2}>
											<FormattedMessage id="StreamChartSettings.Space" defaultMessage="Space" />
										</MenuItem>
										<MenuItem value="," key={4}>
											<FormattedMessage id="StreamChartSettings.Comma" defaultMessage="Comma" />
										</MenuItem>
										<MenuItem value=";" key={3}>
											<FormattedMessage
												id="StreamChartSettings.Semicolon"
												defaultMessage="Semicolon"
											/>
										</MenuItem>
									</TextField>
									{pointIndex === -1 ?
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											margin="normal"
											id="number"
											label={
												<FormattedMessage
													id="StreamChartSettings.LabelRotation"
													defaultMessage="Rotate Labels"
												/>
											}
											inputProps={{
												min: -90,
												max: 90,
												step: 5
											}}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<FormattedMessage
															id="StreamChartSettings.Degrees"
															defaultMessage="Degrees"
														/>
													</InputAdornment>
												)
											}}
											value={
												series.dataLabel.format.fontRotation === undefined
													? 0
													: series.dataLabel.format.fontRotation
											}
											onChange={(event) => handleSeriesDataLabelRotationChange(event)}
											type="number"
										/> : null
									}
									<FormControlLabel
										control={
											<Checkbox
												checked={series.dataLabel.format.linkNumberFormat}
												onChange={(event, state) =>
													handleSeriesDataLabelsLinkChange(event, state)
												}
											/>
										}
										label={
											<FormattedMessage
												id="StreamChartSettings.LinkNumberFormat"
												defaultMessage="Link Numberformat to Cell"
											/>
										}
									/>
								</FormGroup>
							) : null}
							{pointIndex === -1 ? (
								<TextField
									variant="outlined"
									size="small"
									fullWidth
									label={
										<FormattedMessage
											id="StreamChartSettings.TooltipContent"
											defaultMessage="Tooltip Content"
										/>
									}
									select
									margin="normal"
									value={series.tooltip}
									onChange={handleSeriesTooltipContentChange}
								>
									<MenuItem value="hide" key={0}>
										<FormattedMessage id="StreamChartSettings.Hide" defaultMessage="Hide" />
									</MenuItem>
									<MenuItem value="value" key={1}>
										<FormattedMessage id="StreamChartSettings.Value" defaultMessage="Value" />
									</MenuItem>
									<MenuItem value="text" key={2}>
										<FormattedMessage id="StreamChartSettings.Text" defaultMessage="Text" />
									</MenuItem>
								</TextField>
							) : (
								<FormControl>
									<DialogButton marginV onClick={handlePointRemoveSettings}>
										<FormattedMessage
											id="StreamChartSettings.DeletePointSettings"
											defaultMessage="Delete Point Settings"
										/>
									</DialogButton>
								</FormControl>
							)}
						</FormGroup>
					</FormGroup>
				</StreamSheetAccordion>
				<StreamSheetAccordion
					id="panelLegend"
					expanded={expanded === 'panelLegend'}
					onChange={() => setExpanded(expanded === 'panelLegend' ? undefined : 'panelLegend')}
					title={<FormattedMessage id="StreamChartSettings.Legend" defaultMessage="Legend" />}
				>
					{expanded === 'panelLegend' ? (
						<FormGroup>
							<TextField
								variant="outlined"
								size="small"
								margin="normal"
								label={<FormattedMessage id="StreamChartSettings.Title" defaultMessage="Title" />}
								onBlur={(event) => handleLegendTitleFormulaBlur(event)}
								onKeyPress={(event) => {
									if (event.key === 'Enter') {
										handleLegendTitleFormulaBlur(event);
									}
								}}
								InputLabelProps={{ shrink: true }}
								InputProps={{
									inputComponent: MyInputComponent,
									inputProps: {
										component: CellRangeComponent,
										sheetView,
										value: {},
										range: getLocalFormula(chart.legend.formulaTitle, sheetItem)
									}
								}}
							/>
							<FormControl>
								<RadioGroup name="type" value={chart.legend.align} onChange={handleLegendAlignChange}>
									<FormLabel
										component="legend"
										style={{
											marginTop: '7px',
											marginBottom: '7px'
										}}
									>
										<FormattedMessage id="StreamChartSettings.Position" defaultMessage="Position" />
									</FormLabel>
									<FormControlLabel
										value="left"
										control={<Radio />}
										label={<FormattedMessage id="StreamChartSettings.left" defaultMessage="Left" />}
									/>
									<FormControlLabel
										value="middleleft"
										control={<Radio />}
										label={
											<FormattedMessage
												id="StreamChartSettings.middleleft"
												defaultMessage="Middle Left"
											/>
										}
									/>
									<FormControlLabel
										value="top"
										control={<Radio />}
										label={<FormattedMessage id="StreamChartSettings.top" defaultMessage="Top" />}
									/>
									<FormControlLabel
										value="right"
										control={<Radio />}
										label={
											<FormattedMessage id="StreamChartSettings.right" defaultMessage="Right" />
										}
									/>
									<FormControlLabel
										value="middleright"
										control={<Radio />}
										label={
											<FormattedMessage
												id="StreamChartSettings.middleright"
												defaultMessage="Middle Right"
											/>
										}
									/>
									<FormControlLabel
										value="bottom"
										control={<Radio />}
										label={
											<FormattedMessage id="StreamChartSettings.bottom" defaultMessage="Bottom" />
										}
									/>
								</RadioGroup>
							</FormControl>
							<FormControlLabel
								style={{
									marginTop: '10px'
								}}
								control={
									<Checkbox
										checked={chart.legend.visible}
										onChange={(event, state) =>
											handleVisibleChange(event, state, chart.legend, 'legend')
										}
									/>
								}
								label={<FormattedMessage id="StreamChartSettings.Visible" defaultMessage="Visible" />}
							/>
						</FormGroup>
					) : null}
				</StreamSheetAccordion>
				<StreamSheetAccordion
					id="panelTitle"
					expanded={expanded === 'panelTitle'}
					onChange={() => setExpanded(expanded === 'panelTitle' ? undefined : 'panelTitle')}
					title={<FormattedMessage id="StreamChartSettings.Title" defaultMessage="Title" />}
				>
					{expanded === 'panelTitle' ? (
						<FormGroup>
							<TextField
								variant="outlined"
								fullWidth
								size="small"
								margin="normal"
								label={<FormattedMessage id="StreamChartSettings.Title" defaultMessage="Title" />}
								onBlur={(event) => handleTitleFormulaBlur(event)}
								onKeyPress={(event) => {
									if (event.key === 'Enter') {
										handleTitleFormulaBlur(event);
									}
								}}
								InputLabelProps={{ shrink: true }}
								InputProps={{
									inputComponent: MyInputComponent,
									inputProps: {
										component: CellRangeComponent,
										sheetView,
										value: {},
										range: getLocalFormula(chart.title.formula, sheetItem)
									}
								}}
							/>
							<FormControl>
								<RadioGroup name="type" value={chart.title.align} onChange={handleTitleAlignChange}>
									<FormLabel
										component="legend"
										style={{
											marginTop: '5px',
											marginBottom: '5px'
										}}
									>
										<FormattedMessage id="StreamChartSettings.Position" defaultMessage="Position" />
									</FormLabel>
									<FormControlLabel
										value="left"
										control={<Radio />}
										label={<FormattedMessage id="StreamChartSettings.left" defaultMessage="Left" />}
									/>
									<FormControlLabel
										value="center"
										control={<Radio />}
										label={
											<FormattedMessage id="StreamChartSettings.center" defaultMessage="Center" />
										}
									/>
									<FormControlLabel
										value="right"
										control={<Radio />}
										label={
											<FormattedMessage id="StreamChartSettings.right" defaultMessage="Right" />
										}
									/>
								</RadioGroup>
							</FormControl>
							<FormControlLabel
								style={{
									marginTop: '10px'
								}}
								control={
									<Checkbox
										checked={chart.title.visible}
										onChange={(event, state) =>
											handleVisibleChange(event, state, chart.title, 'title')
										}
									/>
								}
								label={<FormattedMessage id="StreamChartSettings.Visible" defaultMessage="Visible" />}
							/>
						</FormGroup>
					) : null}
				</StreamSheetAccordion>
			</div>
			<FormLabel
				component="legend"
				style={{
					padding: '2px',
					border: 'grey 1px solid',
					fontSize: '9pt',
					marginTop: '10px',
					lineHeight: '1.5'
				}}
			>
				<FormattedMessage id="StreamChartSettings.FormatHint" defaultMessage="Format Hint" />
			</FormLabel>
		</div>
	);
}
